import TextField56 from "@/sot/primitives/TextField56.jsx";
import Select from "@/components/ui/Select.jsx";
import Divider from "@/components/ui/Divider.jsx";
import {getPharmacySettlementStatus, listPharmacySettlementBanks, connectPharmacySettlementAccount} from "@/services/pharmacySettlementService.js";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import PharmacyDesktopHeader from "@/components/nav/PharmacyDesktopHeader.jsx";
import ContextFeedbackLauncher from "@/components/feedback/ContextFeedbackLauncher.jsx";
import PrescriptionPatientCell from "@/components/prescriptions/PrescriptionPatientCell.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import TopBar from "@/sot/primitives/TopBar.jsx";
import StockThumb from "@/components/pharmacy/StockThumb.jsx";
import AccountPasswordLifecyclePanel from "@/pages/profile-settings/AccountPasswordLifecyclePanel.jsx";
import { clearLastAuthedPath } from "@/lib/authedPathCache";
import PharmacySettingsMembersPanel from "@/pages/pharmacy/PharmacySettingsMembersPanel.jsx";
import PharmacyFolaSmartCard from "@/pages/pharmacy/PharmacyFolaSmartCard.jsx";
import {
  buildInventorySnapshot,
  derivePharmacyOnboardingTruth,
  getActivePharmacyMemberAccess,
  hasLocalPharmacyAccess,
  isPharmacyConnectOwnershipReady,
  listPharmacyPaymentTransactions,
  loadPharmacySalesSnapshot,
  markPharmacyNotificationsRead,
  PHARMACY_NOTIFICATIONS_CHANGED_EVENT,
  PHARMACY_NOTIFICATIONS_STORAGE_KEY,
  PHARMACY_PROFILE_CHANGED_EVENT,
  PHARMACY_PROFILE_STORAGE_KEY,
  PHARMACY_ROLE_PERMISSION_GROUPS,
  recomputeStockAlerts,
  readPharmacyNotifications,
  readPharmacyProfile,
  PHARMACY_STOCK_PLACEHOLDER,
  resolvePersistedPharmacyAccess,
  resolvePharmacySettlementOwnerUserId,
} from "@/services/pharmacyLaneService.js";
import {
  deletePharmacyInventoryItem,
  listPharmacyInventoryItems,
} from "@/services/pharmacyInventoryService.js";
import {
  resolvePaymentCurrency,
} from "@/services/marketPaymentService.js";
import {
  updatePharmacyFulfillment,
} from "@/lib/payments/api.ts";
import BasicDialog, {
  BASIC_DIALOG_ACTION_CLASS,
  BASIC_DIALOG_SURFACE_CLASS,
  BasicDialogFrame,
} from "@/components/ui/BasicDialog.jsx";
import BasicDialogCritical from "@/components/ui/BasicDialogCritical.jsx";
import {
  clearPasswordMigrationSignal,
  hasPasswordConfigured,
} from "@/features/security/accountSecurityState.js";
import supabase from "@/services/supabaseClient.js";
import { formatCurrency } from "@/utils/formatCurrency.js";
const ICON_NAV_DASHBOARD = "/icons/pharmacy/dashboard.svg";
const ICON_NAV_INVENTORY = "/icons/pharmacy/inventory.svg";
const ICON_NAV_PRESCRIPTIONS = "/icons/pharmacy/received-prescriptions.svg";
const ICON_NAV_PAYMENTS = "/icons/pharmacy/payments.svg";
const ICON_NAV_SETTINGS = "/icons/pharmacy/settings.svg";
const ICON_NAV_HELP = "/icons/pharmacy/help.svg";
const ICON_KPI_RX = "/icons/pharmacy/received-prescriptions.svg";
const ICON_KPI_CONSULT = "/icons/pharmacy/consultations.svg";
const ICON_KPI_SOLD = "/icons/pharmacy/sold-items.svg";
const ICON_TREND = "/icons/pharmacy/arrow_circle_up.svg";
const ICON_CALENDAR = "/icons/pharmacy/calendar-date-range-fill.svg";
const ICON_EXPAND_MORE = "/icons/appointments/dashboard/expand_more.svg";
const ICON_NAVIGATE_NEXT = "/icons/pharmacy/navigate_next.svg";
const CARD_CLASS =
  "rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[var(--elev-1)]";
const ICON_TILE_CLASS =
  "flex h-7 w-7 shrink-0 items-center justify-center rounded-[10px] bg-[rgba(var(--clr-on-surface),0.06)]";
const MUTED_TEXT = "text-[rgb(var(--clr-on-surface-variant))]";
const PRIMARY_TEXT = "text-[rgb(var(--clr-on-surface))]";
const TREND_BG = "bg-[rgba(0,189,196,0.12)]";
const TREND_TEXT = "text-[#008A8F]";
const SECTION_TITLE_CLASS = "text-body-large";
const SURFACE_SECTION_TITLE_CLASS = "text-body-large";
const CARD_LABEL_CLASS = `text-label-medium ${MUTED_TEXT}`;
const CARD_VALUE_CLASS = `text-title-medium ${PRIMARY_TEXT}`;
const EMPHASIS_VALUE_CLASS = `text-title-large ${PRIMARY_TEXT}`;
const DASHBOARD_PANEL_CLASS = CARD_CLASS;
const CHART_TICK_PROPS = {
  className: "text-body-medium fill-[rgb(var(--clr-on-surface-variant))]",
};
const INCOME_RANGE_OPTIONS = [
  { key: "3m", months: 3 },
  { key: "6m", months: 6 },
  { key: "12m", months: 12 },
];

function countMetric(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return new Intl.NumberFormat("en-CH").format(Number(value || 0));
}

function pickProfileText(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function getLastTokenName(value = "") {
  const clean = String(value || "").trim();
  if (!clean || clean.includes("@")) return "";
  const parts = clean.split(/\s+/).filter(Boolean);
  return parts[parts.length - 1] || "";
}

function resolvePharmacyWelcomeTitle(profile = {}) {
  const title = pickProfileText(
    profile?.professionalTitle,
    profile?.professional_title,
    profile?.title,
    profile?.ownerTitle,
    profile?.owner_title,
  );
  const surname =
    pickProfileText(
      profile?.lastName,
      profile?.last_name,
      profile?.ownerLastName,
      profile?.owner_last_name,
      profile?.responsibleLastName,
      profile?.responsible_last_name,
    ) || getLastTokenName(pickProfileText(profile?.adminName, profile?.responsibleLead));

  if (title && surname) return `Welcome ${title} ${surname}`;
  if (surname) return `Welcome ${surname}`;
  return "Welcome";
}

function formatDateTime(value, locale = "en-CH") {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normalizeSearchQuery(value = "") {
  return String(value || "").trim().toLowerCase();
}

function notificationRouteMatchesPath(notification, pathname = "") {
  const route = String(notification?.route || "").trim();
  const currentPath = String(pathname || "").trim();
  if (!route || !currentPath) return false;
  return currentPath === route || currentPath.startsWith(`${route}/`);
}

function DashboardListItemRow({
  title,
  supporting = "",
  supportingSecondary = "",
  image = "",
  trailing = "",
  trailingMuted = "",
  className = "",
}) {
  return (
    <div
      className={[
        "flex min-h-[88px] items-start gap-3 px-4 py-3 sm:px-5",
        className,
      ].join(" ")}
    >
      <StockThumb src={image} alt={title} size="preview" className="shrink-0" />
      <div className="min-w-0 flex-1">
        <p className={`truncate text-body-large ${PRIMARY_TEXT}`}>{title}</p>
        {supporting ? (
          <p className={`mt-px line-clamp-2 text-body-medium ${MUTED_TEXT}`}>{supporting}</p>
        ) : null}
        {supportingSecondary ? (
          <p className={`mt-px text-body-medium ${MUTED_TEXT}`}>{supportingSecondary}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-start gap-0.5 pt-1">
        <div className="text-right">
          <p className={`whitespace-nowrap text-body-large ${MUTED_TEXT}`}>{trailing}</p>
          {trailingMuted ? (
            <p className={`mt-0.5 whitespace-nowrap text-label-small ${MUTED_TEXT}`}>{trailingMuted}</p>
          ) : null}
        </div>
        <img
          src={ICON_NAVIGATE_NEXT}
          alt=""
          className="mt-[2px] h-5 w-5 opacity-60"
          draggable="false"
        />
      </div>
    </div>
  );
}

function CurrencyMenuButton({ currencyCode = "ZAR", open = false, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="
        inline-flex h-10 shrink-0 items-center gap-1.5 rounded-[12px]
        bg-[rgb(var(--clr-surface-cont-highest))]
        px-3 text-label-large text-[rgb(var(--clr-on-surface-variant))]
        hover:bg-[rgba(var(--clr-on-surface),0.04)]
        shadow-[var(--elev-1)]
      "
      aria-haspopup="menu"
      aria-expanded={open}
    >
      <span>{currencyCode}</span>
      <img
        src={ICON_EXPAND_MORE}
        alt=""
        className={["h-4 w-4 opacity-80 transition-transform", open ? "rotate-180" : ""].join(" ")}
        draggable="false"
      />
    </button>
  );
}

function PharmacyPriceField({
  label,
  value,
  onChange,
  currencyCode = "ZAR",
  currencyMenuOpen = false,
  onToggleCurrencyMenu,
  placeholder = "0.00",
}) {
  return (
    <div className="mt-2 w-full">
      <div className="relative w-full rounded-[12px] bg-[rgba(24,50,119,0.04)] px-4 pt-2 pb-2.5">
        <label className="block text-label-medium text-primary">{label}</label>
        <div className="mt-1 flex w-full items-center gap-2">
          {onToggleCurrencyMenu ? (
            <CurrencyMenuButton
              currencyCode={currencyCode}
              open={currencyMenuOpen}
              onToggle={onToggleCurrencyMenu}
            />
          ) : (
            <span className="shrink-0 text-label-large text-on-surface-var">{currencyCode}</span>
          )}
          <input
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(event) => onChange(event?.target?.value || "")}
            placeholder={placeholder}
            className="min-w-0 flex-1 bg-transparent border-0 p-0 text-body-large text-on-surface outline-none placeholder:text-on-surface-var"
          />
        </div>
      </div>
    </div>
  );
}

function PharmacyPriceDialogBody({
  t,
  row,
  draft,
  onDraftChange,
  deliveryDraft,
  onDeliveryDraftChange,
  deliveryConfirmed,
  onDeliveryConfirmedChange,
  error,
  currencyCode = "ZAR",
  currencyMenuOpen = false,
  onToggleCurrencyMenu,
}) {
  return (
    <div className="space-y-4">
      <div className="text-title-medium text-on-surface-var">{row?.id || "—"}</div>

      <div className="rounded-[12px] bg-[rgba(24,50,119,0.04)] px-4 py-3">
        <div className="text-body-large text-on-surface">{row?.patient || "—"}</div>
        <div className="mt-1 text-body-medium text-on-surface-var">{row?.items || "—"}</div>
      </div>

      <PharmacyPriceField
        label={t("pharmacy.prescriptions.medicineSubtotal", "Medicine subtotal")}
        value={draft}
        onChange={onDraftChange}
        currencyCode={currencyCode}
        currencyMenuOpen={currencyMenuOpen}
        onToggleCurrencyMenu={onToggleCurrencyMenu}
      />

      {row?.mode === "Delivery" ? (
        <>
          <PharmacyPriceField
            label={t("pharmacy.prescriptions.deliveryCharge", "Delivery charge")}
            value={deliveryDraft}
            onChange={onDeliveryDraftChange}
            currencyCode={currencyCode}
            currencyMenuOpen={false}
          />
          <div className="rounded-[12px] bg-[rgba(24,50,119,0.04)] px-4 py-3 text-body-medium text-on-surface">
            {t("pharmacy.prescriptions.quoteTotal", "Quote total")}: {formatCurrency(
              Math.max(0, Number(String(draft || "").replace(/[^\d.]/g, "")) || 0) +
                Math.max(0, Number(String(deliveryDraft || "").replace(/[^\d.]/g, "")) || 0),
              currencyCode,
            )}
          </div>
          <label className="flex items-start gap-3 text-body-medium text-on-surface">
            <input
              type="checkbox"
              checked={deliveryConfirmed}
              onChange={(event) => onDeliveryConfirmedChange(event.target.checked)}
              className="mt-1"
            />
            {t("pharmacy.prescriptions.confirmDeliveryQuote", "I confirm the delivery charge and total before requesting payment.")}
          </label>
        </>
      ) : null}

      {error ? <p role="alert" className="text-body-medium text-[rgb(var(--clr-error))]">{error}</p> : null}

      <div className="rounded-[12px] bg-[rgba(24,50,119,0.04)] px-4 py-3 text-body-medium text-on-surface-var">
        {t(
          "pharmacy.prescriptions.priceCurrencyHint",
          "Currency is fixed to this branch payout currency.",
        )}
      </div>

      {currencyMenuOpen ? (
        <div
          className="
            overflow-hidden rounded-[12px]
            bg-[rgb(var(--clr-surface-cont-highest))]
            shadow-[var(--elev-2)]
          "
          role="menu"
          aria-label={t("pharmacy.prescriptions.currencyMenu", "Pharmacy currency")}
        >
          <button
            type="button"
            role="menuitemradio"
            aria-checked="true"
            onClick={onToggleCurrencyMenu}
            className="flex h-11 w-full items-center justify-between px-4 text-left text-body-medium text-on-surface"
          >
            <span>{currencyCode}</span>
            <span className="text-label-small text-[rgb(var(--afrimed-primary))]">
              {t("pharmacy.prescriptions.currencyFixed", "Branch currency")}
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

function matchesSearch(value, query) {
  const needle = normalizeSearchQuery(query);
  if (!needle) return true;
  return String(value || "").toLowerCase().includes(needle);
}

function inventoryRowMatchesQuery(row = {}, query = "") {
  if (!normalizeSearchQuery(query)) return true;
  return [
    row.item,
    row.detail,
    row.category,
    row.stockId,
    row.supplier,
    row.branch,
    row.batchNumber,
  ].some((value) => matchesSearch(value, query));
}

function queueRowMatchesQuery(row = {}, query = "") {
  if (!normalizeSearchQuery(query)) return true;
  return [row.id, row.patient, row.doctor, row.items, row.mode, row.status].some((value) =>
    matchesSearch(value, query),
  );
}

function transactionRowMatchesQuery(row = {}, query = "") {
  if (!normalizeSearchQuery(query)) return true;
  return [
    row.reference,
    row.patientName,
    row.doctorName,
    row.pharmacyName,
    row.status,
    row.paymentIntentId,
  ].some((value) => matchesSearch(value, query));
}

function resolveCurrencyCode(profile) {
  return resolvePaymentCurrency({
    explicitCurrency: profile?.currency || profile?.connect_meta?.currency || profile?.connectMeta?.currency,
    country: profile?.country,
    fallbackCurrency: "ZAR",
  });
}

function renderMoneyTick(value, currencyCode = "ZAR") {
  if (!Number(value || 0)) return "0";
  return formatCurrency(value, currencyCode)
    .replace(/[.,]00\b/, "")
    .replace(/\s00\b/, "");
}

function formatInventoryPrice(row = {}, fallbackCurrency = "ZAR", locale = "en-CH") {
  const amount = Number(row?.unitPriceValue || 0);
  const currency = String(row?.currency || fallbackCurrency || "ZAR").trim().toUpperCase();
  if (amount > 0) return formatCurrency(amount, currency, locale);
  return formatCurrency(0, currency, locale);
}

function formatTooltipDate(value) {
  const date = new Date(value || "");
  if (Number.isNaN(date.getTime())) return "-";
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "2-digit",
      year: "numeric",
    })
      .format(date)
      .toUpperCase();
  } catch {
    return "-";
  }
}

function formatTooltipTrend(current, previous) {
  const curr = Number(current || 0);
  const prev = Number(previous || 0);
  if (prev <= 0) {
    if (curr <= 0) return "0 %";
    return "100 %";
  }
  const raw = ((curr - prev) / prev) * 100;
  return `${Math.abs(Math.round(raw))} %`;
}

function downloadCsv(filename, rows) {
  const csv = rows
    .map((row) =>
      row
        .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
        .join(","),
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function TrendBadge({ children, compact = false }) {
  return (
    <span
      className={[
        TREND_BG,
        TREND_TEXT,
        "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5",
        compact ? "text-label-small" : "text-label-large",
      ].join(" ")}
    >
      <img src={ICON_TREND} alt="" className="h-4 w-4" />
      <span>{children}</span>
    </span>
  );
}

function SurfaceCard({ className = "", children }) {
  return <section className={[CARD_CLASS, className].join(" ")}>{children}</section>;
}

function KpiCard({ label, value, period, trend, icon }) {
  const iconNode =
    typeof icon === "function" ? (
      icon()
    ) : React.isValidElement(icon) ? (
      icon
    ) : (
      <img src={icon} alt="" className="h-5 w-5" />
    );
  return (
    <section className={`${CARD_CLASS} w-full p-5 text-left`}>
      <div className="flex items-center gap-2">
        <span className={ICON_TILE_CLASS}>{iconNode}</span>
        <p className={CARD_LABEL_CLASS}>{label}</p>
      </div>
      <div className={`mt-3 ${CARD_VALUE_CLASS}`}>{value}</div>
      <div className="mt-4 flex items-center gap-3">
        <span className={`text-body-small ${MUTED_TEXT}`}>{period}</span>
        <TrendBadge compact>{trend}</TrendBadge>
      </div>
    </section>
  );
}

function ColdChainIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant))]"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 3.5v4m0 9v4m6.01-14.01-2.83 2.83M8.82 14.18l-2.83 2.83M20.5 12h-4m-9 0h-4m14.51 6.01-2.83-2.83M8.82 9.82 5.99 6.99"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="2.75" fill="currentColor" />
    </svg>
  );
}

function RangePill({ label, expanded = false, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="menu"
      aria-expanded={expanded}
      className="
        inline-flex items-center gap-2
        h-11
        rounded-[12px]
        pl-5 pr-4
        bg-[rgb(var(--clr-surface-cont-highest))]
        shadow-[var(--elev-1)]
        text-label-large text-[rgb(var(--clr-on-surface-variant))]
      "
      aria-label={label}
      title={label}
    >
      <img
        src={ICON_CALENDAR}
        alt=""
        className="h-[20px] w-[20px] opacity-90"
        draggable="false"
      />
      <span className="whitespace-nowrap">{label}</span>
      <img
        src={ICON_EXPAND_MORE}
        alt=""
        className={["h-[18px] w-[18px] opacity-80 transition-transform", expanded ? "rotate-180" : ""].join(" ")}
        draggable="false"
      />
    </button>
  );
}

function IncomeTooltip({ active, payload, currencyCode = "ZAR" }) {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload || {};
  const total = Number(payload[0]?.value || 0);
  const previous = Number(item?.prevTotal || 0);
  const positive = total >= previous;
  const trend = formatTooltipTrend(total, previous);
  return (
    <div
      className="
        rounded-[16px]
        bg-[rgb(var(--clr-surface-container-low))]
        p-[10px]
        border border-[rgba(24,50,119,0.10)]
        shadow-[0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),0px_3px_3px_-1.5px_rgba(26,27,35,0.06),0px_3px_6px_-3px_rgba(26,27,35,0.06),0px_6px_12px_-6px_rgba(29,79,215,0.06)]
      "
    >
      <div className="flex flex-col items-start gap-2">
        <div className="text-label-medium text-[rgb(var(--clr-on-surface-variant))] opacity-60">
          {formatTooltipDate(item?.sourceDate)}
        </div>
        <div className={`text-title-medium ${PRIMARY_TEXT}`}>{formatCurrency(total, currencyCode)}</div>
        <span
          className="
            inline-flex items-center gap-[2px]
            h-6 rounded-[8px]
            pl-1 pr-2
            bg-[rgba(0,189,196,0.12)]
            text-[#008A8F]
            text-body-medium
          "
        >
          {positive ? (
            <img src={ICON_TREND} alt="" className="h-4 w-4" draggable="false" />
          ) : null}
          <span>{trend}</span>
        </span>
      </div>
    </div>
  );
}

function DashboardOverview({
  navigate,
  t,
  profile,
  queue,
  inventorySnapshot,
  salesSnapshot,
  currencyCode,
  salesLoading = false,
}) {
  const [incomeRangeKey, setIncomeRangeKey] = React.useState("12m");
  const [incomeMenuOpen, setIncomeMenuOpen] = React.useState(false);
  const incomeMenuRef = React.useRef(null);
  const incomeRange = React.useMemo(
    () =>
      INCOME_RANGE_OPTIONS.find((option) => option.key === incomeRangeKey) ||
      INCOME_RANGE_OPTIONS[INCOME_RANGE_OPTIONS.length - 1],
    [incomeRangeKey],
  );
  const localizedIncomeRangeLabel = React.useCallback(
    (key) => {
      if (key === "3m") return t("pharmacy.dashboard.income.range.threeMonths", "Past 3 Months");
      if (key === "6m") return t("pharmacy.dashboard.income.range.sixMonths", "Past 6 Months");
      return t("pharmacy.dashboard.income.range.year", "Past Year");
    },
    [t],
  );

  React.useEffect(() => {
    if (!incomeMenuOpen) return undefined;
    function onPointerDown(event) {
      if (!incomeMenuRef.current) return;
      if (!incomeMenuRef.current.contains(event.target)) {
        setIncomeMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [incomeMenuOpen]);

  const reportingUnavailable = Boolean(salesSnapshot?.reportingUnavailable);
  const reportingUnavailableCopy = t(
    "pharmacy.reporting.unavailable",
    "Branch reporting is temporarily unavailable. Please refresh or contact support.",
  );
  const incomeValue = reportingUnavailable
    ? t("pharmacy.reporting.valueUnavailable", "Unavailable")
    : salesLoading
      ? "—"
      : formatCurrency(salesSnapshot?.medicineRevenue, currencyCode);
  const kpiData = [
    {
      key: "rx",
      label: t("pharmacy.dashboard.kpi.received", "Received prescriptions"),
      value: String(queue.length || 0),
      period: t("pharmacy.common.thisMonth", "This Month"),
      trend: t("pharmacy.common.live", "Live"),
      icon: ICON_KPI_RX,
    },
    {
      key: "consultations",
      label: t("pharmacy.dashboard.kpi.consultations", "Consultations"),
      value: salesLoading
        ? "—"
        : reportingUnavailable
        ? t("pharmacy.reporting.valueUnavailable", "Unavailable")
        : countMetric(salesSnapshot?.consultationsCount ?? 0),
      period: t("pharmacy.common.thisMonth", "This Month"),
      trend: salesLoading
        ? "—"
        : reportingUnavailable
        ? t("pharmacy.reporting.checkBranchLink", "Check branch link")
        : countMetric(salesSnapshot?.completedCount ?? 0),
      icon: ICON_KPI_CONSULT,
    },
    {
      key: "sold",
      label: t("pharmacy.dashboard.kpi.sold", "Sold items"),
      value: salesLoading
        ? "—"
        : reportingUnavailable
        ? t("pharmacy.reporting.valueUnavailable", "Unavailable")
        : countMetric(salesSnapshot?.soldItems),
      period: t("pharmacy.common.thisMonth", "This Month"),
      trend: salesLoading
        ? "—"
        : reportingUnavailable
        ? t("pharmacy.reporting.checkBranchLink", "Check branch link")
        : countMetric(salesSnapshot?.completedCount ?? 0),
      icon: ICON_KPI_SOLD,
    },
  ];
  const incomeSeries = Array.isArray(salesSnapshot?.monthlyRevenueSeries)
    ? salesSnapshot.monthlyRevenueSeries
    : [];
  const visibleIncomeSeries = React.useMemo(() => {
    const months = Number(incomeRange?.months || 12);
    if (!Array.isArray(incomeSeries) || months <= 0) return [];
    return incomeSeries.slice(-months);
  }, [incomeRange, incomeSeries]);
  const topMedicines = Array.isArray(salesSnapshot?.topMedicines)
    ? salesSnapshot.topMedicines
    : [];
  const quickActions = [
    { label: "Inventory", route: "/pharmacy/inventory" },
    { label: "Payments", route: "/pharmacy/payments" },
    { label: "Help", route: "/pharmacy/help" },
    { label: "Messages", route: "/pharmacy/messages" },
    { label: "Members", route: "/pharmacy/settings" },
    { label: "Prescriptions", route: "/pharmacy/prescriptions" },
  ];
  return (
    <div className="w-full">
      {reportingUnavailable ? (
        <div className="mb-4 rounded-[12px] bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-on-surface">
          {reportingUnavailableCopy}
        </div>
      ) : null}
      <div className="mb-5 space-y-4">
        <PharmacyFolaSmartCard
          title="Welcome"
          body={"Your pharmacy profile has been created.\n\nWhat would you like to do today?"}
          actions={quickActions}
          actionVariant="tonal"
        />
        <PharmacyFolaSmartCard
          title="Review prescriptions and branch activity"
          body={"Use this dashboard to monitor prescriptions, consultations, sold items, income, and low-stock alerts.\n\nFola can help explain what each card means and what action may be needed next."}
          cta="Ask Fola about dashboard"
          route="/pharmacy/messages/fola"
          promptKey="dashboard_overview"
          returnTo="/pharmacy/dashboard"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {kpiData.map((item) => (
          <KpiCard key={item.key} {...item} />
        ))}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
        <section
          className={[
            DASHBOARD_PANEL_CLASS,
            "min-h-[260px] overflow-hidden md:col-span-2",
          ].join(" ")}
        >
          <div className="flex flex-col gap-5 px-5 py-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className={SURFACE_SECTION_TITLE_CLASS}>{t("pharmacy.dashboard.income.title", "Income")}</p>
                <div className={`mt-2 ${EMPHASIS_VALUE_CLASS}`}>{incomeValue}</div>
                <div className="mt-2 flex items-center gap-2">
                  <span className={`text-label-large opacity-60 ${MUTED_TEXT}`}>
                    {localizedIncomeRangeLabel(incomeRange.key)}
                  </span>
                  <TrendBadge compact>
                    {t("pharmacy.dashboard.income.liveLedger", "Live ledger")}
                  </TrendBadge>
                </div>
              </div>

              <div className="relative" ref={incomeMenuRef}>
                <RangePill
                  label={localizedIncomeRangeLabel(incomeRange.key)}
                  expanded={incomeMenuOpen}
                  onClick={() => setIncomeMenuOpen((open) => !open)}
                />

                {incomeMenuOpen ? (
                  <div
                    className="
                      absolute right-0 mt-2 z-30 min-w-[196px]
                      rounded-[12px]
                      bg-[rgb(var(--clr-surface-cont-highest))]
                      shadow-[var(--elev-2)]
                      overflow-hidden
                    "
                    role="menu"
                    aria-label={t("pharmacy.dashboard.income.menu", "Pharmacy income date range")}
                  >
                    {INCOME_RANGE_OPTIONS.map((option) => {
                      const selected = option.key === incomeRange.key;
                      return (
                        <button
                          key={option.key}
                          type="button"
                          role="menuitemradio"
                          aria-checked={selected}
                          onClick={() => {
                            setIncomeRangeKey(option.key);
                            setIncomeMenuOpen(false);
                          }}
                          className={[
                            "w-full h-10 px-4 text-left text-body-medium",
                            selected
                              ? "bg-[rgba(24,50,119,0.10)] text-on-surface"
                              : "text-on-surface-var hover:bg-[rgba(24,50,119,0.06)]",
                          ].join(" ")}
                        >
                          {localizedIncomeRangeLabel(option.key)}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="h-[320px]">
              {salesLoading ? (
                <div className={`flex h-full items-center justify-center rounded-[12px] bg-[rgba(24,50,119,0.04)] px-6 text-center text-body-medium ${MUTED_TEXT}`}>
                  {t("pharmacy.reporting.loading", "Loading branch reporting…")}
                </div>
              ) : reportingUnavailable ? (
                <div className={`flex h-full items-center justify-center rounded-[12px] bg-[rgba(24,50,119,0.04)] px-6 text-center text-body-medium ${MUTED_TEXT}`}>
                  {reportingUnavailableCopy}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={visibleIncomeSeries} margin={{ top: 12, right: 12, left: -20, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(var(--clr-outline-variant),0.42)" vertical={false} />
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={CHART_TICK_PROPS}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={CHART_TICK_PROPS}
                      tickFormatter={(value) => renderMoneyTick(value, currencyCode)}
                    />
                    <Tooltip
                      cursor={{ stroke: "rgba(var(--clr-on-surface-variant),0.28)", strokeWidth: 1 }}
                      content={<IncomeTooltip currencyCode={currencyCode} />}
                    />
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke="rgb(0 189 196)"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 6, strokeWidth: 2, fill: "rgb(0 189 196)", stroke: "rgb(var(--clr-surface))" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </section>

        <section className={[DASHBOARD_PANEL_CLASS, "overflow-hidden md:col-span-1"].join(" ")}>
          <div className="px-5 py-5">
            <p className={SURFACE_SECTION_TITLE_CLASS}>{t("pharmacy.dashboard.mostSold.title", "Most sold items")}</p>
          </div>
          <div>
            {salesLoading ? (
              <div className={`px-5 py-7 text-body-medium ${MUTED_TEXT}`}>
                {t("pharmacy.reporting.loading", "Loading branch reporting…")}
              </div>
            ) : reportingUnavailable ? (
              <div className={`px-5 py-7 text-body-medium ${MUTED_TEXT}`}>
                {reportingUnavailableCopy}
              </div>
            ) : topMedicines.length ? topMedicines.map((item) => (
              <div
                key={item.name}
                className="border-t border-[rgba(var(--clr-outline-variant),0.48)] first:border-t-0"
              >
                <DashboardListItemRow
                  title={item.name}
                  supporting={item.detail || item.maker}
                  image={item.image}
                  trailing={formatCurrency(item.revenue, currencyCode)}
                />
              </div>
            )) : (
              <div className={`px-5 py-7 text-body-medium ${MUTED_TEXT}`}>
                {t("pharmacy.dashboard.mostSold.empty", "No completed pharmacy sales yet.")}
              </div>
            )}
          </div>
        </section>
      </div>

      <section className={[DASHBOARD_PANEL_CLASS, "mt-5 overflow-hidden"].join(" ")}>
        <div className="flex flex-col gap-3 border-b border-[rgba(var(--clr-outline-variant),0.48)] px-5 py-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className={`${SECTION_TITLE_CLASS} ${PRIMARY_TEXT}`}>{t("pharmacy.dashboard.lowStock.title", "Low Stock")}</p>
            <p className={`mt-1 text-body-medium ${MUTED_TEXT}`}>
              {t("pharmacy.dashboard.lowStock.subtitle", "An overview of low-stock medicine lines that need reordering before prescriptions reroute away.")}
            </p>
          </div>
          <Button
            variant="tonal"
            onClick={() =>
              navigate("/pharmacy/messages/fola", {
                state: {
                  conversationTitle: "Fola",
                  conversationType: "fola",
                  returnTo: "/pharmacy/dashboard",
                  initialDraft: buildLowStockFolaPrompt(t, inventorySnapshot.lowStockRows),
                },
              })
            }
          >
            {t("pharmacy.dashboard.lowStock.openPrompt", "Open Fola prompt")}
          </Button>
        </div>

        {inventorySnapshot.lowStockRows.length ? (
          <>
            <div className="px-5 pb-5 md:hidden">
              {inventorySnapshot.lowStockRows.map((row, index) => (
                <div
                  key={row.stockId}
                  className={index === 0 ? "" : "border-t border-[rgba(var(--clr-outline-variant),0.48)]"}
                >
                  <DashboardListItemRow
                    title={row.item}
                    supporting={row.detail || row.category}
                    supportingSecondary={
                      row.supplier
                        ? `${row.stockId} • ${row.supplier}`
                        : row.stockId
                    }
                    image={row.image}
                    trailing={`${row.currentStock}/${row.minimalStock}`}
                    trailingMuted={formatInventoryPrice(row, currencyCode)}
                  />
                </div>
              ))}
            </div>
            <div className="hidden overflow-x-auto px-5 pb-5 md:block">
              <table className="min-w-[1020px] w-full">
              <thead>
                <tr className={`border-b border-[rgba(var(--clr-outline-variant),0.55)] text-left text-label-medium ${MUTED_TEXT}`}>
                  <th className="px-3 py-4">{t("pharmacy.common.item", "Item")}</th>
                  <th className="px-3 py-4">{t("pharmacy.common.category", "Category")}</th>
                  <th className="px-3 py-4">{t("pharmacy.inventory.stockId", "Stock ID")}</th>
                  <th className="px-3 py-4">{t("pharmacy.inventory.currentStock", "Current Stock")}</th>
                  <th className="px-3 py-4">{t("pharmacy.inventory.minimalStock", "Minimal Stock")}</th>
                  <th className="px-3 py-4">{t("pharmacy.inventory.unitPrice", "Unit Price")}</th>
                  <th className="px-3 py-4">{t("pharmacy.inventory.supplier", "Supplier")}</th>
                </tr>
              </thead>
              <tbody>
                {inventorySnapshot.lowStockRows.map((row) => (
                  <tr key={row.stockId} className="border-b border-[rgba(var(--clr-outline-variant),0.55)] last:border-b-0">
                    <td className="px-3 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-[44px] w-[44px]">
                          <StockThumb src={row.image} alt={row.item} />
                        </div>
                        <div>
                          <p className={`text-body-medium ${PRIMARY_TEXT}`}>{row.item}</p>
                          <p className="text-body-small text-[rgb(var(--clr-on-surface-variant))]">{row.detail}</p>
                        </div>
                      </div>
                    </td>
                    <td className={`px-3 py-4 text-body-medium ${PRIMARY_TEXT}`}>{row.category}</td>
                    <td className={`px-3 py-4 text-body-medium ${MUTED_TEXT}`}>{row.stockId}</td>
                    <td className="px-3 py-4 text-body-medium text-[rgb(var(--clr-error))]">{row.currentStock}</td>
                    <td className={`px-3 py-4 text-body-medium ${PRIMARY_TEXT}`}>{row.minimalStock}</td>
                    <td className={`px-3 py-4 text-body-medium ${PRIMARY_TEXT}`}>{formatInventoryPrice(row, currencyCode)}</td>
                    <td className={`px-3 py-4 text-body-medium ${PRIMARY_TEXT}`}>{row.supplier}</td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="px-5 py-6">
            <div className={`text-title-small ${PRIMARY_TEXT}`}>
              {t("pharmacy.dashboard.lowStock.emptyTitle", "No low-stock lines right now")}
            </div>
            <p className={`mt-1 text-body-medium ${MUTED_TEXT}`}>
              {t(
                "pharmacy.dashboard.lowStock.emptyCopy",
                "AfriMed will surface low-stock medicine lines here once branch inventory is added and tracked against reorder thresholds.",
              )}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

function SectionCard({ title, subtitle = "", actions = null, children, className = "" }) {
  return (
    <SurfaceCard className={["overflow-hidden", className].join(" ")}>
      <div className="flex flex-col gap-3 border-b border-[rgba(var(--clr-outline-variant),0.48)] px-5 py-5 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className={`${SECTION_TITLE_CLASS} ${PRIMARY_TEXT}`}>{title}</h2>
          {subtitle ? <p className={`mt-1 text-body-medium ${MUTED_TEXT}`}>{subtitle}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
      </div>
      <div className="px-5 py-5">{children}</div>
    </SurfaceCard>
  );
}

function getPrescriptionModeLabel(mode, t) {
  if (mode === "Delivery") return t("pharmacy.common.delivery", "Delivery");
  if (mode === "Pickup") return t("pharmacy.common.pickup", "Pickup");
  return mode;
}

function getPrescriptionStatusLabel(status, t) {
  if (status === "pharmacy_selected") return t("pharmacy.prescriptions.status.selected", "Pharmacy selected");
  if (status === "preparing") return t("pharmacy.prescriptions.status.preparing", "Preparing");
  if (status === "awaiting_payment") return t("pharmacy.prescriptions.status.awaitingPayment", "Awaiting payment");
  if (status === "paid") return t("pharmacy.prescriptions.status.paid", "Paid");
  if (status === "ready_for_pickup") return t("pharmacy.prescriptions.status.readyForPickup", "Ready for pickup");
  if (status === "rejected") return t("pharmacy.prescriptions.status.rejected", "Rejected");
  if (status === "Ready to verify") return t("pharmacy.prescriptions.status.readyToVerify", "Ready to verify");
  if (status === "Stock confirmed") return t("pharmacy.prescriptions.status.stockConfirmed", "Stock confirmed");
  if (status === "Courier queued") return t("pharmacy.prescriptions.status.courierQueued", "Courier queued");
  if (status === "Needs stock attention") {
    return t("pharmacy.prescriptions.status.needsStockAttention", "Needs stock attention");
  }
  return status;
}

function getPermissionGroups(t) {
  return PHARMACY_ROLE_PERMISSION_GROUPS.map((group) => ({
    title: t(`pharmacy.settings.permissions.${group.value}.title`, group.title),
    items: t(`pharmacy.settings.permissions.${group.value}.copy`, group.copy),
  }));
}

function StatusPill({ tone = "neutral", children }) {
  const className =
    tone === "success"
      ? `${TREND_BG} ${TREND_TEXT}`
      : tone === "warning"
      ? "bg-[rgba(var(--clr-error),0.10)] text-[rgb(var(--clr-error))]"
      : "bg-[rgba(var(--clr-on-surface-variant),0.12)] text-[rgb(var(--clr-on-surface-variant))]";
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-label-small ${className}`}>{children}</span>;
}

function buildLowStockFolaPrompt(t, rows = []) {
  const lines = (Array.isArray(rows) ? rows : [])
    .slice(0, 6)
    .map(
      (row) =>
        `- ${row.item} (${row.stockId}): on hand ${row.currentStock}, reorder level ${row.minimalStock}`,
    );

  if (!lines.length) {
    return t(
      "pharmacy.folaPrompts.lowStockFallback",
      "Help me draft calm reorder wording and branch handoff notes for pharmacy stock monitoring. Do not assume inventory is auto-synced or make autonomous decisions.",
    );
  }

  return [
    t(
      "pharmacy.folaPrompts.lowStockIntro",
      "Help me draft calm reorder wording and branch handoff notes for these low-stock items.",
    ),
    ...lines,
    t(
      "pharmacy.folaPrompts.lowStockOutro",
      "Stay support-only. Do not assume inventory is auto-synced or make autonomous decisions.",
    ),
  ].join("\n");
}

function InventorySection({
  navigate,
  t,
  languageCode,
  inventorySnapshot,
  currencyCode,
  visibleRows,
  searchQuery = "",
  inventoryLoading = false,
  inventoryError = "",
  onEditRow,
  onRequestDeleteRow,
}) {
  const inventoryKpis = [
    {
      label: t("pharmacy.inventory.kpi.tracked", "Tracked SKUs"),
      value: String(inventorySnapshot.metrics.trackedSkus || 0),
      period: t("pharmacy.inventory.kpi.activeCatalog", "Active catalog"),
      trend: t("pharmacy.common.live", "Live"),
      icon: ICON_KPI_SOLD,
    },
    {
      label: t("pharmacy.inventory.kpi.lowStockAlerts", "Low-stock alerts"),
      value: String(inventorySnapshot.metrics.lowStockAlerts || 0),
      period: t("pharmacy.inventory.kpi.needsAction", "Needs action"),
      trend: String(inventorySnapshot.metrics.lowStockAlerts || 0),
      icon: "/icons/list-items/receipt.svg",
    },
    {
      label: t("pharmacy.inventory.kpi.coldChain", "Cold-chain items"),
      value: String(inventorySnapshot.metrics.coldChainItems || 0),
      period: t("pharmacy.inventory.kpi.verifiedStorage", "Verified storage"),
      trend: countMetric(inventorySnapshot.metrics.coldChainItems || 0),
      icon: ColdChainIcon,
    },
  ];
  const rows = Array.isArray(visibleRows) ? visibleRows : inventorySnapshot.inventoryRows;
  const lowStockPrompt = buildLowStockFolaPrompt(t, inventorySnapshot.lowStockRows);
  return (
    <div className="space-y-7">
      <PharmacyFolaSmartCard
        title="Manage stock availability"
        body={"Keep medicine lines, stock levels, reorder thresholds, prices, suppliers, batches, expiry dates, and storage locations up to date.\n\nAccurate inventory helps the pharmacy provide reliable medicine availability information."}
        cta="Add stock item"
        route="/pharmacy/inventory/new"
        returnTo="/pharmacy/inventory"
      />
      <div className="grid gap-5 md:grid-cols-3">
        {inventoryKpis.map((item) => (
          <KpiCard key={item.label} {...item} />
        ))}
      </div>

      <SectionCard
        title={t("pharmacy.inventory.stockControl.title", "Stock control")}
        subtitle={t("pharmacy.inventory.stockControl.subtitle", "Manage medicine availability so AfriMed can show signed-up branch availability, stock confidence, and patient-facing pricing clearly.")}
        actions={
          <>
            <Button
              variant="tonal"
              onClick={() =>
                navigate("/pharmacy/messages/fola", {
                  state: {
                    conversationTitle: "Fola",
                    conversationType: "fola",
                    returnTo: "/pharmacy/inventory",
                    initialDraft: lowStockPrompt,
                  },
                })
              }
            >
              {t("pharmacy.inventory.actions.openFolaPrompt", "Open Fola prompt")}
            </Button>
            <Button onClick={() => navigate("/pharmacy/inventory/new")}>
              {t("pharmacy.inventory.actions.addStockItem", "Add stock item")}
            </Button>
          </>
        }
      >
        {inventoryError ? (
          <div className="mb-4 rounded-[12px] bg-[rgba(var(--clr-error),0.10)] px-4 py-3 text-body-medium text-[rgb(var(--clr-error))]">
            {inventoryError}
          </div>
        ) : null}
        {normalizeSearchQuery(searchQuery) ? (
          <div className={`mb-4 text-body-medium ${MUTED_TEXT}`}>
            {t("pharmacy.search.results", "Search results")} • {rows.length}
          </div>
        ) : null}
        {inventoryLoading ? (
          <div className={`py-4 text-body-medium ${MUTED_TEXT}`}>
            {t("pharmacy.inventory.loading", "Loading branch inventory…")}
          </div>
        ) : rows.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-[880px] w-full">
              <thead>
                <tr className={`border-b border-[rgba(var(--clr-outline-variant),0.55)] text-left text-label-medium ${MUTED_TEXT}`}>
                  <th className="px-3 py-4">{t("pharmacy.common.item", "Item")}</th>
                  <th className="px-3 py-4">{t("pharmacy.inventory.onHand", "On hand")}</th>
                  <th className="px-3 py-4">{t("pharmacy.inventory.reorderLevel", "Reorder level")}</th>
                  <th className="px-3 py-4">{t("pharmacy.inventory.retailPrice", "Retail price")}</th>
                  <th className="px-3 py-4">{t("pharmacy.common.status", "Status")}</th>
                  <th className="px-3 py-4">{t("common.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.stockId} className="border-b border-[rgba(var(--clr-outline-variant),0.55)] last:border-b-0">
                    <td className="px-3 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-[44px] w-[44px]">
                          <StockThumb src={row.image} alt={row.item} />
                        </div>
                        <div>
                          <div className={`text-body-large ${PRIMARY_TEXT}`}>{row.item}</div>
                          <div className={`text-body-small ${MUTED_TEXT}`}>{row.stockId}</div>
                        </div>
                      </div>
                    </td>
                    <td className={`px-3 py-4 text-body-medium ${PRIMARY_TEXT}`}>{row.currentStock}</td>
                    <td className={`px-3 py-4 text-body-medium ${PRIMARY_TEXT}`}>{row.minimalStock}</td>
                    <td className={`px-3 py-4 text-body-medium ${PRIMARY_TEXT}`}>
                      {formatInventoryPrice(row, currencyCode, languageCode || "en-CH")}
                    </td>
                    <td className="px-3 py-4">
                      <StatusPill tone={Number(row.currentStock || 0) <= Number(row.minimalStock || 0) ? "warning" : "success"}>
                        {Number(row.currentStock || 0) <= Number(row.minimalStock || 0)
                          ? t("pharmacy.inventory.reorderNow", "Reorder now")
                          : t("pharmacy.inventory.healthy", "Healthy")}
                      </StatusPill>
                    </td>
                    <td className="px-3 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="text"
                          onClick={() => onEditRow?.(row)}
                        >
                          {t("common.edit", "Edit")}
                        </Button>
                        <Button
                          size="sm"
                          variant="text"
                          className="text-[rgb(var(--clr-error))] hover:bg-[rgba(var(--clr-error),0.08)]"
                          onClick={() => onRequestDeleteRow?.(row)}
                        >
                          {t("common.remove", "Remove")}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-2">
            <div className={`text-title-small ${PRIMARY_TEXT}`}>
              {t("pharmacy.inventory.empty.title", "No stock items added yet")}
            </div>
            <p className={`mt-1 text-body-medium ${MUTED_TEXT}`}>
              {t(
                "pharmacy.inventory.empty.copy",
                "Add your first stock item so low-stock alerts, branch availability, and prescription routing can read from real branch data.",
              )}
            </p>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function PrescriptionsSection({
  navigate,
  t,
  queue,
  visibleQueue = [],
  searchQuery = "",
  priceDrafts = {},
  actionBusyById = {},
  onPriceDraftChange,
  onOpenPriceDialog,
  onFulfillmentAction,
  actionError = "",
  currencyCode = "ZAR",
  queueLoading = false,
}) {
  const selectedCount = queue.filter((row) => row.status === "pharmacy_selected").length;
  const preparingCount = queue.filter((row) => row.status === "preparing").length;
  const awaitingPaymentCount = queue.filter((row) => row.status === "awaiting_payment").length;
  const readyCount = queue.filter((row) => row.status === "ready_for_pickup").length;
  const queueKpis = [
    {
      label: t("pharmacy.prescriptions.kpi.newRx", "New prescriptions"),
      value: String(selectedCount || 0),
      period: t("pharmacy.prescriptions.kpi.liveQueue", "Live queue"),
      trend: String(selectedCount || 0),
      icon: ICON_KPI_RX,
    },
    {
      label: t("pharmacy.prescriptions.kpi.preparing", "Preparing"),
      value: String(preparingCount),
      period: t("pharmacy.prescriptions.kpi.counterHandoff", "Accepted by pharmacy"),
      trend: String(preparingCount),
      icon: ICON_KPI_CONSULT,
    },
    {
      label: t("pharmacy.prescriptions.kpi.release", "Release"),
      value: String(readyCount),
      period: t("pharmacy.prescriptions.kpi.readyForPickup", "Ready for pickup"),
      trend: String(awaitingPaymentCount + readyCount),
      icon: ICON_NAV_INVENTORY,
    },
  ];
  return (
    <div className="space-y-7">
      <PharmacyFolaSmartCard
        title="Process prescription requests"
        body="Review incoming prescriptions, open each order, confirm stock, set the pharmacy price, and prepare the item for pickup or delivery once payment is confirmed."
        returnTo="/pharmacy/prescriptions"
      />
      <div className="grid gap-5 md:grid-cols-3">
        {queueKpis.map((item) => (
          <KpiCard key={item.label} {...item} />
        ))}
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-[720px]">
          <h2 className={`${SECTION_TITLE_CLASS} ${PRIMARY_TEXT}`}>{t("pharmacy.prescriptions.title", "AfriMed™ prescription processing")}</h2>
          <p className={`mt-1 text-body-medium ${MUTED_TEXT}`}>
            {t("pharmacy.prescriptions.subtitle", "Accept new prescriptions, reject when you cannot fulfil them, set the quote, and release the order once payment is confirmed.")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
          <ContextFeedbackLauncher
            section="pharmacy-prescription-queue"
            roleOverride="pharmacy"
            entity={{ id: "prescription-queue", label: "prescription queue", type: "queue" }}
            context={{
              totalQueue: queue.length,
              visibleQueue: visibleQueue.length,
              searchQuery,
            }}
            triggerLabel={t("feedback.trigger", "Make suggestion")}
            subtitle={t(
              "pharmacy.prescriptions.feedbackSubtitle",
              "Capture this queue state so AfriMed can reopen the exact pharmacy prescription context.",
            )}
          />
          <Button variant="tonal" onClick={() => navigate("/pharmacy/prescriptions/scan")}>
            {t("pharmacy.prescriptions.actions.openScanner", "Open scanner")}
          </Button>
          <Button onClick={() => navigate("/pharmacy/inventory")}>{t("pharmacy.prescriptions.actions.checkStock", "Check stock")}</Button>
        </div>
      </div>

      <div className="space-y-5">
        {actionError ? (
          <div className="rounded-[12px] bg-[rgba(var(--clr-error),0.10)] px-4 py-3 text-body-medium text-[rgb(var(--clr-error))]">
            {actionError}
          </div>
        ) : null}

        {normalizeSearchQuery(searchQuery) ? (
          <div className={`text-body-medium ${MUTED_TEXT}`}>
            {t("pharmacy.search.results", "Search results")} • {visibleQueue.length}
          </div>
        ) : null}

        {queueLoading ? (
          <div className={`${CARD_CLASS} p-6 text-body-medium ${MUTED_TEXT}`}>
            {t("pharmacy.prescriptions.loading", "Loading branch prescription requests…")}
          </div>
        ) : visibleQueue.length ? visibleQueue.map((row) => (
          <div key={row.id} className={`${CARD_CLASS} p-5`}>
            <div className="space-y-4">
              <div className={`text-title-medium ${PRIMARY_TEXT}`}>{row.id}</div>

              <div className="min-w-0">
                <PrescriptionPatientCell
                  name={row.patient}
                  subtitle={row.doctor}
                  avatarUrl={row.patientAvatarUrl || row.prescriberAvatarUrl}
                  avatarSizeClass="h-12 w-12"
                  nameClassName={`text-title-small ${PRIMARY_TEXT}`}
                  subtitleClassName={`text-body-small ${MUTED_TEXT}`}
                />
                {row.careSubjectType === "dependant" && row.guardianName ? (
                  <div className={`mt-2 text-body-small ${MUTED_TEXT}`}>
                    {t("pharmacy.prescriptions.responsibleAdult", "Responsible adult")}:{" "}
                    <span className={PRIMARY_TEXT}>
                      {row.guardianName}
                      {row.responsibleRelationship ? ` • ${row.responsibleRelationship}` : ""}
                    </span>
                  </div>
                ) : null}
                <div className={`mt-3 text-body-medium ${MUTED_TEXT}`}>{row.items}</div>
              </div>

              <div className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-4">
                <div>
                  <div className={`text-label-medium ${MUTED_TEXT}`}>{t("pharmacy.common.mode", "Mode")}</div>
                  <div className={`mt-1 text-body-medium ${PRIMARY_TEXT}`}>{getPrescriptionModeLabel(row.mode, t)}</div>
                </div>
                <div>
                  <div className={`text-label-medium ${MUTED_TEXT}`}>{t("pharmacy.common.subtotal", "Subtotal")}</div>
                  <div className={`mt-1 text-body-medium ${PRIMARY_TEXT}`}>{row.subtotal}</div>
                </div>
                <div>
                  <div className={`text-label-medium ${MUTED_TEXT}`}>{t("pharmacy.common.delivery", "Delivery")}</div>
                  <div className={`mt-1 text-body-medium ${PRIMARY_TEXT}`}>{row.delivery}</div>
                </div>
                <div>
                  <div className={`text-label-medium ${MUTED_TEXT}`}>{t("pharmacy.common.status", "Status")}</div>
                  <div className="mt-2">
                    <StatusPill
                      tone={
                        row.status === "rejected"
                          ? "warning"
                          : row.status === "awaiting_payment" || row.status === "preparing" || row.status === "paid" || row.status === "ready_for_pickup"
                            ? "success"
                            : "neutral"
                      }
                    >
                      {getPrescriptionStatusLabel(row.status, t)}
                    </StatusPill>
                  </div>
                </div>
              </div>
            </div>
            {row.status === "preparing" ? (
              <div className="mt-5 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => onOpenPriceDialog(row)}
                    disabled={Boolean(actionBusyById[row.fulfillmentId])}
                  >
                    {actionBusyById[row.fulfillmentId]
                      ? t("common.status.saving", "Saving…")
                      : t("pharmacy.prescriptions.actions.setPrice", "Set price")}
                  </Button>
                  <Button
                    size="sm"
                    variant="tonal"
                    onClick={() => onFulfillmentAction(row, "reject")}
                    disabled={Boolean(actionBusyById[row.fulfillmentId])}
                  >
                    {t("pharmacy.prescriptions.actions.reject", "Reject")}
                  </Button>
              </div>
            ) : null}
            <div className="mt-5 flex flex-wrap gap-2">
              <Button size="sm" variant="tonal" onClick={() => navigate(row.qrPath)}>
                {t("pharmacy.prescriptions.actions.openOrder", "Open order")}
              </Button>
              {row.status === "pharmacy_selected" ? (
                <>
                  <Button
                    size="sm"
                    onClick={() => onFulfillmentAction(row, "accept")}
                    disabled={Boolean(actionBusyById[row.fulfillmentId])}
                  >
                    {actionBusyById[row.fulfillmentId]
                      ? t("common.status.saving", "Saving…")
                      : t("pharmacy.prescriptions.actions.accept", "Accept for review")}
                  </Button>
                  <Button
                    size="sm"
                    variant="tonal"
                    onClick={() => onFulfillmentAction(row, "reject")}
                    disabled={Boolean(actionBusyById[row.fulfillmentId])}
                  >
                    {t("pharmacy.prescriptions.actions.reject", "Reject")}
                  </Button>
                </>
              ) : null}
              {row.status === "awaiting_payment" ? (
                <div className={`inline-flex items-center rounded-full px-3 py-2 text-label-small ${TREND_BG} ${TREND_TEXT}`}>
                  {row.priceValue > 0
                    ? t("pharmacy.prescriptions.priceSet", "Price set: {subtotal}", {
                        subtotal: row.subtotal,
                      })
                    : t("pharmacy.prescriptions.priceReady", "Price ready")}
                </div>
              ) : null}
              {row.status === "paid" ? (
                <Button
                  size="sm"
                  onClick={() => onFulfillmentAction(row, "mark_ready_for_pickup")}
                  disabled={Boolean(actionBusyById[row.fulfillmentId])}
                >
                  {actionBusyById[row.fulfillmentId]
                    ? t("common.status.saving", "Saving…")
                    : t("pharmacy.prescriptions.actions.markReadyForPickup", "Mark ready for pickup")}
                </Button>
              ) : null}
            </div>
          </div>
        )) : (
          <div className={`${CARD_CLASS} p-6 text-body-medium ${MUTED_TEXT}`}>
            {normalizeSearchQuery(searchQuery)
              ? t("pharmacy.prescriptions.searchEmpty", "No pharmacy prescriptions match this search yet.")
              : t("pharmacy.prescriptions.empty", "No issued prescriptions are waiting in this pharmacy queue right now.")}
          </div>
        )}
      </div>
    </div>
  );
}

function PaymentsSection({
  t,
  salesSnapshot,
  currencyCode,
  visibleTransactions = [],
  searchQuery = "",
  profile = null,
  onExportCsv = null,
  pricedPrescriptions = [],
}) {
  const auth = useAuth() || {};
  const location = useLocation();
  const transactionRows = Array.isArray(salesSnapshot?.transactions)
    ? salesSnapshot.transactions
    : [];
  const filteredTransactions = Array.isArray(visibleTransactions) ? visibleTransactions : transactionRows;
  const reportingUnavailable = Boolean(salesSnapshot?.reportingUnavailable);
  const reportingUnavailableCopy = t(
    "pharmacy.reporting.unavailable",
    "Branch reporting is temporarily unavailable. Please refresh or contact support.",
  );
  const [linkState, setLinkState] = React.useState({
    loading: false,
    url: "",
    error: "",
    feeAmount: 0,
    sellerNetAmount: 0,
    currency: currencyCode,
    paymentLinkId: "",
  });
  const currentUserId = String(auth?.user?.id || "").trim();
  const currentUserEmail = String(auth?.user?.email || "").trim().toLowerCase();
  const branchName = String(
    profile?.pharmacyName || profile?.tradingName || salesSnapshot?.branchName || "AfriMed™ Pharmacy branch",
  ).trim();
  const [connectState, setConnectState] = React.useState({
    loading: true,
    submitting: false,
    status: {},
    error: "",
  });
  const [settlementDialogOpen, setSettlementDialogOpen] = React.useState(false);
  const [settlementBanks, setSettlementBanks] = React.useState([]);
  const [settlementForm, setSettlementForm] = React.useState({
    businessName: String(profile?.tradingName || profile?.pharmacyName || "").trim(),
    contactName: "",
    bankCode: "",
    accountNumber: "",
  });
  const connectReady = Boolean(connectState.status?.connected) && String(connectState.status?.account_status || "").toLowerCase() === "active";
  const [selectedPrescription, setSelectedPrescription] = React.useState('');
  const payable = pricedPrescriptions.filter(row => row.status === 'awaiting_payment' && row.prescriptionId && Number(row.priceValue) > 0 && !row.deliveryQuoteIncomplete);
  const selected = payable.find(row => row.prescriptionId === selectedPrescription);
  const canGenerateLink = connectReady && Boolean(selected);

  React.useEffect(() => {
    let alive = true;

    async function hydrateSettlementState() {
      if (!currentUserId) {
        if (alive) {
          setConnectState({
            loading: false,
            submitting: false,
            status: {},
            error: "",
          });
        }
        return;
      }

      try {
        if (alive) {
          setConnectState((current) => ({
            ...current,
            loading: true,
            error: "",
          }));
        }
        const nextStatus = await getPharmacySettlementStatus();
        if (!alive) return;
        setConnectState((current) => ({
          ...current,
          loading: false,
          status: nextStatus || {},
          error: "",
        }));
      } catch (error) {
        if (!alive) return;
        setConnectState((current) => ({
          ...current,
          loading: false,
          status: current.status || {},
          error:
            settlementErrorMessage(error) ||
            t(
              "pharmacy.payments.connect.statusError",
              "We could not confirm the pharmacy payout status right now.",
            ),
        }));
      }
    }

    hydrateSettlementState();
    return () => {
      alive = false;
    };
  }, [currentUserId, t]);

  const paymentCards = [
    {
      label: t("pharmacy.payments.kpi.medicineRevenue", "Medicine revenue"),
      value: reportingUnavailable
        ? t("pharmacy.reporting.valueUnavailable", "Unavailable")
        : formatCurrency(salesSnapshot?.medicineRevenue, currencyCode),
      period: t("pharmacy.payments.kpi.past30Days", "Past 30 days"),
      trend: countMetric(transactionRows.length || 0),
      icon: ICON_NAV_PAYMENTS,
    },
    {
      label: t("pharmacy.payments.kpi.afrimedFee", "AfriMed™ fee"),
      value: reportingUnavailable
        ? t("pharmacy.reporting.valueUnavailable", "Unavailable")
        : formatCurrency(salesSnapshot?.afrimedFee, currencyCode),
      period: t("pharmacy.payments.kpi.subtotalOnly", "10% subtotal only"),
      trend: t("pharmacy.payments.kpi.onTrack", "On track"),
      icon: "/icons/list-items/receipt.svg",
    },
    {
      label: t("pharmacy.payments.kpi.deliveryCollected", "Delivery collected"),
      value: reportingUnavailable
        ? t("pharmacy.reporting.valueUnavailable", "Unavailable")
        : formatCurrency(salesSnapshot?.deliveryCollected, currencyCode),
      period: t("pharmacy.payments.kpi.excludedFromFee", "Excluded from fee"),
      trend: countMetric(
        transactionRows.filter((row) => Number(row?.deliveryFee || 0) > 0).length || 0,
      ),
      icon: ICON_NAV_INVENTORY,
    },
  ];

  const connectHeadline = connectState.loading
    ? t("pharmacy.payments.connect.loadingTitle", "Checking settlement readiness...")
    : connectReady
      ? t("pharmacy.payments.connect.readyTitle", "Settlement setup is ready")
      : t("pharmacy.payments.connect.blockedTitle", "Connect settlement setup before creating payment links");
  const connectCopy = connectState.loading
    ? t(
        "pharmacy.payments.connect.loadingBody",
        "AfriMed™ is checking the saved pharmacy settlement status so payment-link ownership can unlock against the correct payout account.",
      )
    : connectReady
      ? t(
          "pharmacy.payments.connect.readyBody",
          "Your pharmacy settlement account is connected.",
        )
      : t(
          "pharmacy.payments.connect.blockedBody",
          "Set up this pharmacy’s settlement details so AfriMed™ can route pharmacy payments and payouts securely. Payment-link generation remains locked until the destination is ready.",
        );
  const connectButtonLabel = connectState.submitting
    ? t("pharmacy.payments.connect.opening", "Opening settlement setup...")
    : connectReady
      ? t("pharmacy.payments.connect.manage", "Manage settlement setup")
      : t("pharmacy.payments.connect.cta", "Connect settlement account");
  const connectStatusLabel = connectState.loading
    ? t("common.loading", "Loading...")
    : connectReady
      ? t("pharmacy.payments.connect.statusReady", "Connected")
      : t("pharmacy.payments.connect.statusBlocked", "Required");
  const connectStatusTone = connectReady
    ? "bg-[rgba(34,197,94,0.12)] text-[rgb(22,163,74)]"
    : "bg-[rgba(29,79,215,0.12)] text-[rgb(var(--afrimed-primary))]";
  const settlementErrorMessage = (error) => {
    const message = String(error?.message || "").trim();
    if (/branch member|branch owner/i.test(message)) {
      return "This pharmacy account is linked as a branch member. Sign in with the branch owner account to manage settlement onboarding.";
    }
    return message.replace(/stripe connect/gi, "settlement onboarding").replace(/stripe/gi, "settlement");
  };

  async function handleSettlementConnect() {
    if (connectState.submitting || !currentUserId) return;

    try {
      setConnectState((current) => ({
        ...current,
        submitting: true,
        error: "",
      }));

      if (connectReady) return;
      const result = await listPharmacySettlementBanks();
      const banks = Array.isArray(result?.banks) ? result.banks : [];
      if (!banks.length) throw new Error("Settlement banks are temporarily unavailable. Please try again.");
      setSettlementBanks(banks);
      setSettlementForm((current) => ({ ...current, bankCode: current.bankCode || String(banks[0]?.code || "") }));
      setSettlementDialogOpen(true);
      setConnectState((current) => ({ ...current, submitting: false }));
    } catch (error) {
      setConnectState((current) => ({
        ...current,
        submitting: false,
        error:
            settlementErrorMessage(error) ||
          t(
            "pharmacy.payments.connect.launchError",
            "Settlement onboarding could not be opened for this pharmacy right now.",
          ),
      }));
    }
  }

  async function handleSettlementSubmit() {
    if (connectState.submitting) return;
    try {
      setConnectState((current) => ({ ...current, submitting: true, error: "" }));
      const nextStatus = await connectPharmacySettlementAccount({
        business_name: settlementForm.businessName,
        contact_name: settlementForm.contactName,
        contact_email: currentUserEmail,
        bank_code: settlementForm.bankCode,
        account_number: settlementForm.accountNumber,
      });
      setConnectState((current) => ({ ...current, submitting: false, status: nextStatus || {}, error: "" }));
      setSettlementDialogOpen(false);
    } catch (error) {
      setConnectState((current) => ({ ...current, submitting: false, error: settlementErrorMessage(error) || "We could not save the settlement setup. Please try again." }));
    }
  }

  function patchForm(patch = {}) {
    setForm((current) => ({ ...current, ...patch }));
  }

  function renderStatusChip(status = "") {
    const normalized = String(status || "").trim().toLowerCase();
    const label =
      normalized === "refunded"
        ? t("pharmacy.payments.status.refunded", "Refunded")
        : normalized === "refund_pending"
          ? t("pharmacy.payments.status.refundPending", "Refund pending")
          : normalized === "paid" || normalized === "succeeded" || normalized === "completed"
            ? t("pharmacy.payments.status.paid", "Paid")
            : normalized === "requires_payment"
              ? t("pharmacy.payments.status.awaitingPayment", "Awaiting payment")
              : t("pharmacy.payments.status.pending", "Pending");
    const toneClass =
      normalized === "refunded"
        ? "bg-[rgba(0,189,196,0.12)] text-[#008A8F]"
        : normalized === "refund_pending"
          ? "bg-[rgba(24,50,119,0.08)] text-[rgb(var(--clr-on-surface))]"
          : normalized === "paid" || normalized === "succeeded" || normalized === "completed"
            ? "bg-[rgba(34,197,94,0.12)] text-[rgb(22,163,74)]"
            : "bg-[rgba(24,50,119,0.08)] text-[rgb(var(--clr-on-surface))]";

    return (
      <span className={`inline-flex rounded-full px-2.5 py-1 text-label-small ${toneClass}`}>
        {label}
      </span>
    );
  }

  async function handleGeneratePaymentLink() {
    if (!canGenerateLink) return;
    // Share the existing authenticated Patient checkout. No charge, bearer token,
    // sponsor mandate or invented quote is created in the pharmacy browser.
    setLinkState({loading:false,url:`${window.location.origin}/appointments/prescriptions/${selected.prescriptionId}/pay`,error:'',feeAmount:0,sellerNetAmount:0,currency:currencyCode,paymentLinkId:''});
  }

  async function handleCopyLink() {
    if (!linkState.url) return;
    try {
      await navigator.clipboard.writeText(linkState.url);
      setLinkState((current) => ({ ...current, error: "" }));
    } catch {
      setLinkState((current) => ({
        ...current,
        error: t("pharmacy.payments.link.copyError", "We couldn’t copy the payment link right now."),
      }));
    }
  }

  return (
    <div className="space-y-7">
      <PharmacyFolaSmartCard
        title="Understand pharmacy settlements"
        body="Review medicine revenue, AfriMed™ fees, delivery collections, payment readiness, generated payment links, and pharmacy transactions."
        cta="Ask Fola about payments"
        route="/pharmacy/messages/fola"
        promptKey="payments_settlement"
        returnTo="/pharmacy/payments"
      />
      {reportingUnavailable ? (
        <div className="rounded-[12px] bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-on-surface">
          {reportingUnavailableCopy}
        </div>
      ) : null}
      <div className="grid gap-5 md:grid-cols-3">
        {paymentCards.map((card) => (
          <Card key={card.label} className="p-5">
            <div className="flex items-center gap-2">
              <span className={ICON_TILE_CLASS}>
                <img src={card.icon} alt="" className="h-5 w-5" />
              </span>
              <p className={CARD_LABEL_CLASS}>{card.label}</p>
            </div>
            <div className={`mt-3 ${CARD_VALUE_CLASS}`}>{card.value}</div>
            <div className="mt-4 flex items-center gap-3">
              <span className={`text-body-small ${MUTED_TEXT}`}>{card.period}</span>
              <TrendBadge compact>{card.trend}</TrendBadge>
            </div>
          </Card>
        ))}
      </div>

      <div>
        <h2 className={`${SECTION_TITLE_CLASS} ${PRIMARY_TEXT}`}>
          {t("pharmacy.payments.settlement.title", "Settlement logic")}
        </h2>
        <p className={`mt-1 text-body-medium ${MUTED_TEXT}`}>
          {t(
            "pharmacy.payments.settlement.subtitle",
            "Keep medicine subtotal, delivery, and tax visibly separated for pharmacy accounting and patient trust.",
          )}
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {[
          [
            t("pharmacy.payments.settlement.subtotal", "Medicine subtotal"),
            reportingUnavailable
              ? t("pharmacy.reporting.valueUnavailable", "Unavailable")
              : formatCurrency(salesSnapshot?.medicineRevenue, currencyCode),
            t("pharmacy.payments.settlement.subtotalCopy", "This is the only base AfriMed™ uses for the 10% fee."),
          ],
          [
            t("pharmacy.payments.settlement.delivery", "Delivery collected"),
            reportingUnavailable
              ? t("pharmacy.reporting.valueUnavailable", "Unavailable")
              : formatCurrency(salesSnapshot?.deliveryCollected, currencyCode),
            t("pharmacy.payments.settlement.deliveryCopy", "Delivery belongs to the logistics side and stays outside platform commission."),
          ],
          [
            t("pharmacy.payments.settlement.tax", "Tax handled"),
            formatCurrency(0, currencyCode),
            t("pharmacy.payments.settlement.taxCopy", "Add country-specific tax handling in the backend lane when pharmacy billing goes live."),
          ],
        ].map(([label, value, copy]) => (
          <Card key={label} className="p-5">
            <div className={CARD_LABEL_CLASS}>{label}</div>
            <div className={`mt-2 ${CARD_VALUE_CLASS}`}>{value}</div>
            <p className={`mt-3 text-body-small ${MUTED_TEXT}`}>{copy}</p>
          </Card>
        ))}
      </div>

      <section id="pharmacy-stripe-connect-zone" className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <div className="max-w-[44rem]">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={`${SECTION_TITLE_CLASS} ${PRIMARY_TEXT}`}>
                {t("pharmacy.payments.connect.title", "Settlement setup")}
              </h2>
              <span className={`inline-flex rounded-full px-2.5 py-1 text-label-small ${connectStatusTone}`}>
                {connectStatusLabel}
              </span>
            </div>
            <div className={`mt-2 text-title-small ${PRIMARY_TEXT}`}>{connectHeadline}</div>
            <p className={`mt-2 text-body-medium ${MUTED_TEXT}`}>{connectCopy}</p>
            <p className={`mt-3 text-body-small ${MUTED_TEXT}`}>
              {t(
                "pharmacy.payments.connect.methodsCopy",
                "AfriMed™ routes supported online payments and pharmacy settlements through approved payment partners. Availability varies by market.",
              )}
            </p>
          </div>
          <Button
            onClick={handleSettlementConnect}
            disabled={connectState.loading || connectState.submitting}
            className="w-full sm:w-auto"
          >
            {connectButtonLabel}
          </Button>
        </div>

        {connectState.error ? (
          <div className="rounded-[12px] bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-on-surface">
            {connectState.error}
          </div>
        ) : null}

        <BasicDialog
          open={settlementDialogOpen}
          title={t("pharmacy.payments.connect.dialogTitle", "Settlement setup")}
          body={
            <div className="space-y-4">
              <p className={`text-body-medium ${MUTED_TEXT}`}>
                {t("pharmacy.payments.connect.dialogCopy", "Add the bank account AfriMed™ should use for this pharmacy’s settlements.")}
              </p>
              <TextField56
                label={t("pharmacy.payments.connect.businessName", "Business name")}
                value={settlementForm.businessName}
                onChange={(businessName) => setSettlementForm((current) => ({ ...current, businessName }))}
                autoComplete="organization"
                tone="surface"
              />
              <TextField56
                label={t("pharmacy.payments.connect.contactName", "Account holder or contact name")}
                value={settlementForm.contactName}
                onChange={(contactName) => setSettlementForm((current) => ({ ...current, contactName }))}
                autoComplete="name"
                tone="surface"
              />
              <div className="block">
                <span className={CARD_LABEL_CLASS}>{t("pharmacy.payments.connect.bank", "Settlement bank")}</span>
                <Select
                  className="mt-1"
                  value={settlementForm.bankCode}
                  onValueChange={(bankCode) => setSettlementForm((current) => ({ ...current, bankCode }))}
                  options={settlementBanks.map((bank) => ({ value: bank.code, label: bank.name }))}
                  placeholder={t("pharmacy.payments.connect.bankPlaceholder", "Choose a bank")}
                  searchable
                  searchTreatment="menu-text-field"
                  mobileSheet
                  mobileSheetTitle={t("pharmacy.payments.connect.bank", "Settlement bank")}
                  contentClassName="max-h-[320px]"
                />
              </div>
              <TextField56
                label={t("pharmacy.payments.connect.accountNumber", "Account number")}
                value={settlementForm.accountNumber}
                onChange={(accountNumber) => setSettlementForm((current) => ({ ...current, accountNumber }))}
                inputMode="numeric"
                autoComplete="off"
                tone="surface"
              />
            </div>
          }
          cancelText={t("common.cancel", "Cancel")}
          okText={connectState.submitting ? t("common.saving", "Saving…") : t("pharmacy.payments.connect.save", "Save settlement details")}
          onCancel={() => (connectState.submitting ? null : setSettlementDialogOpen(false))}
          onOk={handleSettlementSubmit}
          surfaceClassName="!overflow-visible"
        />
      </section>

      <section id="pharmacy-payment-link-zone" className="space-y-4">
        <div className="max-w-[44rem]">
          <h2 className={`${SECTION_TITLE_CLASS} ${PRIMARY_TEXT}`}>
            {t("pharmacy.payments.link.title", "Create payment link")}
          </h2>
          <p className={`mt-1 text-body-medium ${MUTED_TEXT}`}>
            {t(
              "pharmacy.payments.link.subtitle",
              "Create a payment link for a priced prescription.",
            )}
          </p>
          {!connectReady ? (
            <p className="mt-3 text-body-small text-[rgb(var(--afrimed-primary))]">
              {t(
                "pharmacy.payments.link.lockedCopy",
                "Connect your settlement account before creating payment links.",
              )}
            </p>
          ) : null}
        </div>

        <div className="space-y-4">
          <Select id="payment-prescription" label={t('pharmacy.payments.link.pricedPrescription','Priced prescription')}
            value={selectedPrescription} disabled={!connectReady}
            placeholder={t('pharmacy.payments.link.selectPrescription','Select a prescription awaiting payment')}
            options={payable.map(row=>({value:row.prescriptionId,label:`${row.id} · ${row.patient} · ${row.total}`}))}
            onValueChange={value=>{setSelectedPrescription(value);setLinkState(current=>({...current,url:'',error:''}));}} />
          <p className="text-body-small text-on-surface-var">{t('pharmacy.payments.link.patientCheckout','The Patient signs in to review the pharmacy quote. Payment methods depend on the active provider and market. Staging payments are tests.')}</p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={handleGeneratePaymentLink} disabled={!canGenerateLink || linkState.loading}>
              {linkState.loading
                ? t("common.loading", "Loading...")
                : t("pharmacy.payments.link.generate", "Generate payment link")}
            </Button>
            <Button variant="tonal" onClick={handleCopyLink} disabled={!connectReady || !linkState.url}>
              {t("pharmacy.payments.link.copy", "Copy link")}
            </Button>

          </div>

          <p className={`text-body-small ${MUTED_TEXT}`}>
            {t(
              "pharmacy.payments.link.footnote",
              "AfriMed™ charges 10% of the medicine subtotal. Delivery is excluded.",
            )}
          </p>
        </div>

        {linkState.url && <a data-testid="prescription-payment-link" className="block break-all text-primary" href={linkState.url}>{linkState.url}</a>}
        {linkState.error ? (
          <div className="rounded-[12px] bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-on-surface">
            {linkState.error}
          </div>
        ) : null}
      </section>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className={`${SECTION_TITLE_CLASS} ${PRIMARY_TEXT}`}>
            {t("pharmacy.payments.transactions.title", "Recent pharmacy transactions")}
          </h2>
          <p className={`mt-1 max-w-[620px] text-body-medium ${MUTED_TEXT}`}>
            {t(
              "pharmacy.payments.transactions.subtitle",
              "Review paid, refunded, and awaiting-payment prescription totals from the pharmacy ledger.",
            )}
          </p>
        </div>
        <Button
          variant="tonal"
          className="shrink-0 self-start"
          onClick={onExportCsv || undefined}
          disabled={!onExportCsv}
        >
          {t("pharmacy.payments.actions.exportCsv", "Export CSV")}
        </Button>
      </div>

      <div data-testid="pharmacy-transactions">
        {normalizeSearchQuery(searchQuery) ? (
          <div className={`px-5 py-4 text-body-medium ${MUTED_TEXT}`}>
            {t("pharmacy.search.results", "Search results")} • {filteredTransactions.length}
          </div>
        ) : null}
        {reportingUnavailable ? (
          <div className="px-4 py-5">
            <div className={`text-title-small ${PRIMARY_TEXT}`}>
              {t("pharmacy.reporting.titleUnavailable", "Branch reporting unavailable")}
            </div>
            <p className={`mt-1 text-body-medium ${MUTED_TEXT}`}>
              {reportingUnavailableCopy}
            </p>
          </div>
        ) : filteredTransactions.length ? (
          <div>
            {filteredTransactions.map((row, index) => (
              <React.Fragment key={row.id}>
              {index > 0 && <Divider />}
              <div
                key={row.id}
                className="px-5 py-5"
              >
                <div className="space-y-4 md:hidden">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className={`text-title-small ${PRIMARY_TEXT}`}>
                        {row.patientName || t("pharmacy.payments.transactions.walkIn", "Patient payment")}
                      </div>
                      <div className={`mt-1 text-body-small ${MUTED_TEXT} break-all`}>
                        {row.reference
                          ? t("pharmacy.payments.transactions.reference", "Reference") + ` • ${row.reference}`
                          : formatDateTime(row.createdAt)}
                      </div>
                      <div className={`mt-2 text-body-small ${MUTED_TEXT}`}>
                        {row.doctorName
                          ? t("pharmacy.payments.transactions.doctorPrefix", "Prescriber") + ` • ${row.doctorName}`
                          : formatDateTime(row.createdAt)}
                      </div>
                    </div>
                    <div className="shrink-0 pt-0.5">
                      {renderStatusChip(row.status)}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-x-5 gap-y-4">
                    <div>
                      <div className={`text-label-medium ${MUTED_TEXT}`}>
                        {t("pharmacy.payments.transactions.medicine", "Medicine subtotal")}
                      </div>
                      <div className={`mt-1 text-body-medium ${PRIMARY_TEXT}`}>
                        {formatCurrency(row.medicineSubtotal, row.currency || currencyCode)}
                      </div>
                    </div>
                    <div>
                      <div className={`text-label-medium ${MUTED_TEXT}`}>
                        {t("pharmacy.payments.transactions.delivery", "Delivery")}
                      </div>
                      <div className={`mt-1 text-body-medium ${PRIMARY_TEXT}`}>
                        {formatCurrency(row.deliveryFee, row.currency || currencyCode)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start justify-between gap-4 border-t border-[rgba(24,50,119,0.08)] pt-4">
                    <div className="min-w-0">
                      <div className={`text-body-small ${MUTED_TEXT}`}>
                        {t("pharmacy.payments.transactions.afrimedFee", "AfriMed™ fee")}{" "}
                        {formatCurrency(row.afrimedFee, row.currency || currencyCode)}
                      </div>
                      <div className={`mt-1 text-body-small ${MUTED_TEXT}`}>
                        {t("pharmacy.payments.transactions.net", "Pharmacy net")}{" "}
                        {formatCurrency(row.pharmacyNet, row.currency || currencyCode)}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className={`text-title-medium ${PRIMARY_TEXT}`}>
                        {formatCurrency(row.grossAmount, row.currency || currencyCode)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="hidden gap-4 md:grid md:grid-cols-[1.3fr_1fr_0.8fr]">
                  <div className="min-w-0">
                    <div className={`text-title-small ${PRIMARY_TEXT}`}>
                      {row.patientName || t("pharmacy.payments.transactions.walkIn", "Patient payment")}
                    </div>
                    <div className={`mt-1 text-body-small ${MUTED_TEXT}`}>
                      {row.reference
                        ? t("pharmacy.payments.transactions.reference", "Reference") + ` • ${row.reference}`
                        : formatDateTime(row.createdAt)}
                    </div>
                    <div className={`mt-2 text-body-small ${MUTED_TEXT}`}>
                      {row.doctorName
                        ? t("pharmacy.payments.transactions.doctorPrefix", "Prescriber") + ` • ${row.doctorName}`
                        : formatDateTime(row.createdAt)}
                    </div>
                  </div>

                  <div className="grid gap-1 sm:grid-cols-2 md:grid-cols-1">
                    <div>
                      <div className={`text-label-medium ${MUTED_TEXT}`}>
                        {t("pharmacy.payments.transactions.medicine", "Medicine subtotal")}
                      </div>
                      <div className={`mt-1 text-body-medium ${PRIMARY_TEXT}`}>
                        {formatCurrency(row.medicineSubtotal, row.currency || currencyCode)}
                      </div>
                    </div>
                    <div>
                      <div className={`text-label-medium ${MUTED_TEXT}`}>
                        {t("pharmacy.payments.transactions.delivery", "Delivery")}
                      </div>
                      <div className={`mt-1 text-body-medium ${PRIMARY_TEXT}`}>
                        {formatCurrency(row.deliveryFee, row.currency || currencyCode)}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-start gap-2 md:items-end">
                    {renderStatusChip(row.status)}
                    <div className="text-right">
                      <div className={`text-title-medium ${PRIMARY_TEXT}`}>
                        {formatCurrency(row.grossAmount, row.currency || currencyCode)}
                      </div>
                      <div className={`mt-1 text-body-small ${MUTED_TEXT}`}>
                        {t("pharmacy.payments.transactions.afrimedFee", "AfriMed™ fee")}{" "}
                        {formatCurrency(row.afrimedFee, row.currency || currencyCode)}
                      </div>
                      <div className={`mt-1 text-body-small ${MUTED_TEXT}`}>
                        {t("pharmacy.payments.transactions.net", "Pharmacy net")}{" "}
                        {formatCurrency(row.pharmacyNet, row.currency || currencyCode)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="px-4 py-5">
            <div className={`text-title-small ${PRIMARY_TEXT}`}>
              {normalizeSearchQuery(searchQuery)
                ? t("pharmacy.payments.transactions.searchEmpty", "No pharmacy payment records match this search")
                : t("pharmacy.payments.transactions.empty", "No pharmacy payment records yet")}
            </div>
            <p className={`mt-1 text-body-medium ${MUTED_TEXT}`}>
              {normalizeSearchQuery(searchQuery)
                ? t(
                    "pharmacy.payments.transactions.searchEmptySubtitle",
                    "Try a patient name, prescriber, payment reference, or status.",
                  )
                : t(
                    "pharmacy.payments.transactions.emptySubtitle",
                    "New prescription payment rows will appear here once a quoted pharmacy order is paid or refunded.",
                  )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function SettingsSection({ profile, t }) {
  const permissionGroups = getPermissionGroups(t);
  return (
    <div className="space-y-7">
      <PharmacySettingsMembersPanel profile={profile} t={t} />

      <div>
        <h2 className={`${SECTION_TITLE_CLASS} ${PRIMARY_TEXT}`}>
          {t("pharmacy.settings.privilegeHandoff", "Privilege handoff")}
        </h2>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        {permissionGroups.map((group) => (
          <Card key={group.title} className="p-5">
            <div className={`text-title-small ${PRIMARY_TEXT}`}>{group.title}</div>
            <p className={`mt-1 text-body-small ${MUTED_TEXT}`}>{group.items}</p>
          </Card>
        ))}
      </div>

      <div>
        <h2 className={`${SECTION_TITLE_CLASS} ${PRIMARY_TEXT}`}>{t("pharmacy.settings.verification.title", "Verification status")}</h2>
        <p className={`mt-1 text-body-medium ${MUTED_TEXT}`}>
          {t("pharmacy.settings.verification.subtitle", "A clean view of what the branch provided during onboarding.")}
        </p>
      </div>

      <section aria-labelledby="pharmacy-branch-summary-title">
        <h3 id="pharmacy-branch-summary-title" className={SURFACE_SECTION_TITLE_CLASS}>
          {t("pharmacy.settings.branchSummary", "Branch summary")}
        </h3>
        <dl className="mt-4 grid grid-cols-3 gap-x-5 gap-y-4">
          <div className="min-w-0 text-center sm:text-left">
            <dt className={`text-label-medium ${MUTED_TEXT}`}>{t("pharmacy.settings.license", "License")}</dt>
            <dd className={`mt-1 text-body-large ${PRIMARY_TEXT}`}>{profile?.licenseNumber || t("pharmacy.common.notSet", "Not set")}</dd>
          </div>
          <div className="min-w-0 text-center sm:text-left">
            <dt className={`text-label-medium ${MUTED_TEXT}`}>{t("pharmacy.settings.responsibleLead", "Responsible lead")}</dt>
            <dd className={`mt-1 text-body-large ${PRIMARY_TEXT}`}>{profile?.adminName || t("pharmacy.common.notSet", "Not set")}</dd>
          </div>
          <div className="min-w-0 text-center sm:text-left">
            <dt className={`text-label-medium ${MUTED_TEXT}`}>{t("pharmacy.common.delivery", "Delivery")}</dt>
            <dd className={`mt-1 text-body-large ${PRIMARY_TEXT}`}>{profile?.deliveryEnabled ? t("pharmacy.common.enabled", "Enabled") : t("pharmacy.common.disabled", "Disabled")}</dd>
          </div>
        </dl>
      </section>
    </div>
  );
}

function HelpSection({ t }) {
  const helpSections = [
    {
      title: t("pharmacy.help.gettingStarted.title", "Getting started as a pharmacy"),
      copy: t(
        "pharmacy.help.gettingStarted.copy",
        "Start with the seeded dashboard: review the notification bell, open the sample prescription requests, and use the demo stock rows to learn the pharmacy workspace before adding real branch data.",
      ),
      actions: [
        t("pharmacy.help.gettingStarted.action1", "Open the Dashboard first and confirm the demo revenue, sold items, and prescription count are visible."),
        t("pharmacy.help.gettingStarted.action2", "Use the Prescriptions page to process the three sample fulfilment requests."),
        t("pharmacy.help.gettingStarted.action3", "Use Inventory and Payments to understand how stock and settlement data appear in the workspace."),
      ],
    },
    {
      title: t("pharmacy.help.profile.title", "Pharmacy profile and verification"),
      copy: t(
        "pharmacy.help.profile.copy",
        "Your branch profile tells AfriMed™ where the pharmacy is, who is responsible for the branch, and which verification items still need real-world review.",
      ),
      actions: [
        t("pharmacy.help.profile.action1", "Keep the branch name, trading name, address, and responsible lead easy for testers to recognise."),
        t("pharmacy.help.profile.action2", "Verification and payout readiness may remain pending during review."),
        t("pharmacy.help.profile.action3", "Pending verification does not mean public approval or public branch availability."),
      ],
    },
    {
      title: t("pharmacy.help.inventory.title", "Inventory management"),
      copy: t(
        "pharmacy.help.inventory.copy",
        "Inventory helps AfriMed™ show branch availability and stock confidence. Review branches receive demo stock so testers can inspect the workflow immediately.",
      ),
      actions: [
        t("pharmacy.help.inventory.action1", "Review seeded stock rows before adding or editing stock."),
        t("pharmacy.help.inventory.action2", "Use stock counts and reorder levels to understand low-stock alerts."),
        t("pharmacy.help.inventory.action3", "Product images are demo assets and should remain stable across reloads."),
      ],
    },
    {
      title: t("pharmacy.help.fulfilment.title", "Prescription fulfilment"),
      copy: t(
        "pharmacy.help.fulfilment.copy",
        "Process clinician-issued prescriptions through preparation, stock confirmation, pricing, payment handoff, and dispensing status updates.",
      ),
      actions: [
        t("pharmacy.help.fulfilment.action1", "Verify the prescription before acting on it."),
        t("pharmacy.help.fulfilment.action2", "Accept only when the branch can fulfil the prescription safely."),
        t("pharmacy.help.fulfilment.action3", "Update status as work moves from review to preparation, payment, pickup, delivery, or completion."),
      ],
    },
    {
      title: t("pharmacy.help.payments.title", "Payments and settlement"),
      copy: t(
        "pharmacy.help.payments.copy",
        "Payment examples use demo records only. Paid demo fulfilments appear in revenue reporting so testers can confirm dashboard totals and transaction history.",
      ),
      actions: [
        t("pharmacy.help.payments.action1", "Paid demo rows count toward dashboard income."),
        t("pharmacy.help.payments.action2", "Pending demo rows should not count as paid revenue."),
        t("pharmacy.help.payments.action3", "Stripe or payout readiness must not block invited reviewers from exploring the dashboard."),
      ],
    },
    {
      title: t("pharmacy.help.messages.title", "Pharmacy Messages and Fola™"),
      copy: t(
        "pharmacy.help.messages.copy",
        "Messages and Fola™ stay inside the pharmacy workspace. Use Fola™ for pharmacy-safe operational guidance, not clinical advice or prescription routing decisions.",
      ),
      actions: [
        t("pharmacy.help.messages.action1", "Ask Fola™ about branch roles, CSV prep, inventory setup, handoff steps, and workflow limitations."),
        t("pharmacy.help.messages.action2", "Do not use Fola™ to diagnose patients, override doctors, or choose pharmacy routing."),
        t("pharmacy.help.messages.action3", "Use the Pharmacy header to move between Dashboard, Inventory, Payments, Messages, and Help."),
      ],
    },
    {
      title: t("pharmacy.help.team.title", "Team members and permissions"),
      copy: t(
        "pharmacy.help.team.copy",
        "Owners can invite branch staff and assign controlled roles. Staff should only receive the smallest role needed for the task they are testing.",
      ),
      actions: [
        t("pharmacy.help.team.action1", "Owner/test owner: manage branch profile, staff, settings, fulfilment activity, and pricing or quote review."),
        t("pharmacy.help.team.action2", "Dispensing pharmacist: review prescriptions, confirm stock, prepare or dispense medication, and update fulfilment status."),
        t("pharmacy.help.team.action3", "Assistant/staff roles cannot manage ownership, payment settings, pharmacy removal, or staff invitations unless explicitly allowed."),
      ],
    },
    {
      title: t("pharmacy.help.notifications.title", "Notifications and alerts"),
      copy: t(
        "pharmacy.help.notifications.copy",
        "The notification bell highlights prescription routing and stock attention items. New seeded review branches should show three sample prescription alerts.",
      ),
      actions: [
        t("pharmacy.help.notifications.action1", "Use the bell count to confirm active prescription requests are visible."),
        t("pharmacy.help.notifications.action2", "Opening the prescription queue should show the same branch-scoped requests."),
        t("pharmacy.help.notifications.action3", "If notification count and prescription rows disagree, report it as a data hydration issue."),
      ],
    },
  ];

  const playbookSteps = [
    [
      t("pharmacy.help.step1.title", "1. Verify the prescription"),
      t("pharmacy.help.step1.copy", "Use the QR scanner to confirm the AfriMed prescription before dispensing."),
    ],
    [
      t("pharmacy.help.step2.title", "2. Confirm stock and price"),
      t("pharmacy.help.step2.copy", "Show patient-facing availability, medicine subtotal, and delivery cost clearly."),
    ],
    [
      t("pharmacy.help.step3.title", "3. Hand off cleanly"),
      t("pharmacy.help.step3.copy", "Mark pickup or delivery completed so the doctor, patient, and pharmacy stay aligned."),
    ],
  ];

  return (
    <div className="space-y-8">
      <div>
        <p className="text-label-large text-[rgb(var(--afrimed-primary))]">
          {t("pharmacy.help.eyebrow", "Pharmacy Help Centre")}
        </p>
        <h2 className={`mt-2 ${SECTION_TITLE_CLASS} ${PRIMARY_TEXT}`}>
          {t("pharmacy.help.title", "Run the Pharmacy workspace")}
        </h2>
        <p className={`mt-2 max-w-[760px] text-body-medium ${MUTED_TEXT}`}>
          {t(
            "pharmacy.help.subtitle",
            "Use these guides to understand seeded demo stock, sample prescription fulfilment, payments, team access, notifications, and Fola™.",
          )}
        </p>
      </div>

      <PharmacyFolaSmartCard
        title="Find pharmacy guidance"
        body="Use this page to search pharmacy guidance on onboarding, profile verification, inventory, prescription fulfilment, payments, messages, alerts, team roles, and technical support."
        actions={[
          {
            label: "Messages & Fola",
            route: "/pharmacy/messages/fola",
            promptKey: "pharmacy_help",
          },
          {
            label: "Technical support",
            onClick: () => {
              const target = document.getElementById("pharmacy-help-technical-support");
              target?.scrollIntoView({ behavior: "smooth", block: "start" });
            },
          },
        ]}
        returnTo="/pharmacy/help"
      />

      <section className={`${CARD_CLASS} overflow-hidden`}>
        <div className="border-b border-[rgba(var(--clr-outline-variant),0.48)] px-5 py-5">
          <h3 className={`${SURFACE_SECTION_TITLE_CLASS} ${PRIMARY_TEXT}`}>
            {t("pharmacy.help.quickStart.title", "Quick start for reviewers")}
          </h3>
          <p className={`mt-2 text-body-medium ${MUTED_TEXT}`}>
            {t(
              "pharmacy.help.quickStart.copy",
              "After launching your Pharmacy Dashboard, you will find seeded demo stock and up to three sample prescription fulfilment requests. Please use these to explore how AfriMed™ supports pharmacy preparation, verification, dispensing, payments, and fulfilment workflows.",
            )}
          </p>
        </div>
        <div className="grid gap-0 md:grid-cols-3">
          {playbookSteps.map(([title, copy], index) => (
            <div
              key={title}
              className={[
                "min-h-[144px] px-5 py-5",
                index > 0 ? "border-t border-[rgba(var(--clr-outline-variant),0.48)] md:border-l md:border-t-0" : "",
              ].join(" ")}
            >
              <div className={SURFACE_SECTION_TITLE_CLASS}>{title}</div>
              <p className={`mt-2 text-body-medium ${MUTED_TEXT}`}>{copy}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="grid gap-5 lg:grid-cols-3">
        {helpSections.map((section) => (
          <div
            key={section.title}
            id={section.title === t("pharmacy.help.technical.title", "Technical support") ? "pharmacy-help-technical-support" : undefined}
            className={`${CARD_CLASS} flex min-h-[280px] flex-col p-5`}
          >
            <div className={SURFACE_SECTION_TITLE_CLASS}>{section.title}</div>
            <p className={`mt-2 text-body-medium ${MUTED_TEXT}`}>{section.copy}</p>
            <ul className="mt-4 space-y-2">
              {section.actions.map((action) => (
                <li key={action} className="flex gap-2 text-body-small text-[rgb(var(--clr-on-surface-variant))]">
                  <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[rgb(var(--afrimed-primary))]" />
                  <span>{action}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <section className={`${CARD_CLASS} p-5`}>
        <h3 className={`${SURFACE_SECTION_TITLE_CLASS} ${PRIMARY_TEXT}`}>
          {t("pharmacy.help.safeBoundaries.title", "Review boundaries")}
        </h3>
        <p className={`mt-2 max-w-[860px] text-body-medium ${MUTED_TEXT}`}>
          {t(
            "pharmacy.help.safeBoundaries.copy",
            "Demo pharmacy data is test data only. It helps testers understand fulfilment workflows, but it is not public approval, real patient data, real payment capture, or prescription routing.",
          )}
        </p>
      </section>
    </div>
  );
}

function clearPharmacyMagicLinkTransitionState(user) {
  clearPasswordMigrationSignal(user);
  clearLastAuthedPath(user?.id || "");
  if (typeof window === "undefined") return;
  const keys = [
    "afrimed:pending_magic_email",
    "afrimed:pending_magic_sent_at",
    "afrimed:pending_magic_context",
    "afrimed:pharmacy:access:v1",
  ];
  try {
    keys.forEach((key) => {
      window.localStorage.removeItem(key);
      window.sessionStorage.removeItem(key);
    });
  } catch {
    // ignore storage cleanup failures
  }
}

function PharmacyPasswordLifecycleDialog({ user, email = "", userId = "" }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [checkingAccess, setCheckingAccess] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(false);
  const passwordMarker = `${user?.user_metadata?.password_set === true ? "1" : "0"}:${
    user?.user_metadata?.password_changed_at || ""
  }`;

  React.useEffect(() => {
    let alive = true;
    const cleanUserId = String(userId || user?.id || "").trim();
    const cleanEmail = String(email || user?.email || "").trim().toLowerCase();

    async function resolvePasswordPromptAccess() {
      if (!cleanUserId || hasPasswordConfigured(user)) {
        setCheckingAccess(false);
        setOpen(false);
        return;
      }

      setCheckingAccess(true);
      try {
        const access = await resolvePersistedPharmacyAccess({
          userId: cleanUserId,
          email: cleanEmail,
        });
        const isOwner =
          access?.hasOwnerProfile === true ||
          (Array.isArray(access?.rows) &&
            access.rows.some((row) => String(row?.owner_user_id || "").trim() === cleanUserId));
        const isMember = !!getActivePharmacyMemberAccess(access, {
          userId: cleanUserId,
          email: cleanEmail,
        });
        if (!alive) return;
        setOpen(Boolean(isOwner || isMember));
      } catch (accessError) {
        console.warn("[PharmacyWorkspace] create-password access check skipped:", accessError?.message || accessError);
        if (alive) setOpen(false);
      } finally {
        if (alive) setCheckingAccess(false);
      }
    }

    resolvePasswordPromptAccess();
    return () => {
      alive = false;
    };
  }, [email, passwordMarker, user, userId]);

  async function handlePasswordCreated() {
    setOpen(false);
    setConfirmed(true);
  }

  async function handleConfirmation() {
    clearPharmacyMagicLinkTransitionState(user);
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch {
      // Password creation succeeded; route to login even if local cleanup has already happened.
    } finally {
      if (typeof window !== "undefined") {
        window.location.replace("/login");
        return;
      }
      navigate("/login", { replace: true });
    }
  }

  if (checkingAccess || (!open && !confirmed)) return null;

  if (confirmed) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/35" />
        <BasicDialogFrame
          role="dialog"
          aria-modal="true"
          title={t("pharmacy.password.createdTitle", "Password created")}
          body={
            <span>
              {t("pharmacy.password.createdBody1", "You will now be logged out.")}
              <br />
              {t("pharmacy.password.createdBody2", "Enter your email and new password")}
              <br />
              {t("pharmacy.password.createdBody3", "to log back in.")}
            </span>
          }
          contentClassName="pt-7 pb-3 sm:pt-8 sm:pb-4"
          actionsClassName="pt-2 sm:pt-3"
        >
          <button
            type="button"
            className={`${BASIC_DIALOG_ACTION_CLASS} mx-auto max-w-[220px]`}
            style={{
              background: "rgb(var(--afrimed-primary))",
              color: "rgb(var(--afrimed-on-primary))",
              borderColor: "rgb(var(--afrimed-primary))",
            }}
            onClick={handleConfirmation}
          >
            {t("common.understood", "Understood")}
          </button>
        </BasicDialogFrame>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/35" />
      <div
        className={`${BASIC_DIALOG_SURFACE_CLASS} px-6 pb-6 pt-7 sm:px-7 sm:pb-7 sm:pt-8`}
        role="dialog"
        aria-modal="true"
      >
        <AccountPasswordLifecyclePanel
          className="text-left"
          intent="create"
          resetBasePath="/pharmacy/dashboard"
          loginSecurityPath="/pharmacy/dashboard"
          clearPasswordMigrationOnSuccess={false}
          onCreatePasswordSuccess={handlePasswordCreated}
        />
      </div>
    </div>
  );
}

export default function PharmacyWorkspace({ section = "dashboard" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth() || {};
  const { t, languageCode } = useLanguage();
  const currentUserId = String(auth?.user?.id || "").trim();
  const currentUserEmail = String(auth?.user?.email || "").trim().toLowerCase();
  const activeActorRef = React.useRef(currentUserId);
  activeActorRef.current = currentUserId;
  const readCurrentProfile = () => {
    const local = readPharmacyProfile();
    return hasLocalPharmacyAccess(local, { userId: currentUserId, email: currentUserEmail })
      ? local
      : null;
  };
  const [profile, setProfile] = React.useState(readCurrentProfile);
  const [notifications, setNotifications] = React.useState(() =>
    readCurrentProfile() ? readPharmacyNotifications() : [],
  );
  const [inventoryRows, setInventoryRows] = React.useState(() => {
    const snapshot = buildInventorySnapshot(readCurrentProfile(), {
      fallbackInventoryRows: [],
      fallbackFeaturedItems: [],
    });
    return Array.isArray(snapshot.inventoryRows) ? snapshot.inventoryRows : [];
  });
  const [inventoryLoading, setInventoryLoading] = React.useState(true);
  const [inventoryError, setInventoryError] = React.useState("");
  const [deleteCandidate, setDeleteCandidate] = React.useState(null);
  const [deletingInventory, setDeletingInventory] = React.useState(false);
  const [queue, setQueue] = React.useState([]);
  const [priceDrafts, setPriceDrafts] = React.useState({});
  const [pricingDialogOpen, setPricingDialogOpen] = React.useState(false);
  const [pricingDialogRow, setPricingDialogRow] = React.useState(null);
  const [pricingDialogDraft, setPricingDialogDraft] = React.useState("");
  const [pricingDeliveryDraft, setPricingDeliveryDraft] = React.useState("0");
  const [pricingDeliveryConfirmed, setPricingDeliveryConfirmed] = React.useState(false);
  const [pricingCurrencyMenuOpen, setPricingCurrencyMenuOpen] = React.useState(false);
  const [actionBusyById, setActionBusyById] = React.useState({});
  const [queueActionError, setQueueActionError] = React.useState("");
  const [queueLoading, setQueueLoading] = React.useState(true);
  const [salesLoading, setSalesLoading] = React.useState(true);
  const [paymentsRefreshToken, setPaymentsRefreshToken] = React.useState(0);
  const [salesSnapshot, setSalesSnapshot] = React.useState({
    hasLiveData: false,
    soldItems: null,
    medicineRevenue: null,
    deliveryCollected: null,
    afrimedFee: null,
    transactions: [],
  });
  const searchQuery = React.useMemo(() => {
    return String(new URLSearchParams(location.search || "").get("q") || "").trim();
  }, [location.search]);

  React.useEffect(() => {
    // A newly signed-in pharmacy must never inherit the preceding account's
    // queue, quote drafts, stock or transaction snapshot while it hydrates.
    setQueue([]);
    setPriceDrafts({});
    setProfile(readCurrentProfile());
    setNotifications(readCurrentProfile() ? readPharmacyNotifications() : []);
    setInventoryRows([]);
    setSalesSnapshot({
      hasLiveData: false,
      soldItems: null,
      medicineRevenue: null,
      deliveryCollected: null,
      afrimedFee: null,
      transactions: [],
    });
  }, [currentUserId]);

  React.useEffect(() => {
    const refreshProfile = () => setProfile(readCurrentProfile());
    const refreshNotifications = () =>
      setNotifications(readCurrentProfile() ? readPharmacyNotifications() : []);
    const handleStorage = (event) => {
      if (!event?.key || event.key === PHARMACY_PROFILE_STORAGE_KEY) refreshProfile();
      if (!event?.key || event.key === PHARMACY_NOTIFICATIONS_STORAGE_KEY) refreshNotifications();
    };

    refreshProfile();
    refreshNotifications();
    window.addEventListener(PHARMACY_PROFILE_CHANGED_EVENT, refreshProfile);
    window.addEventListener(PHARMACY_NOTIFICATIONS_CHANGED_EVENT, refreshNotifications);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(PHARMACY_PROFILE_CHANGED_EVENT, refreshProfile);
      window.removeEventListener(PHARMACY_NOTIFICATIONS_CHANGED_EVENT, refreshNotifications);
      window.removeEventListener("storage", handleStorage);
    };
  }, [currentUserEmail, currentUserId]);

  React.useEffect(() => {
    const unreadRouteIds = (Array.isArray(notifications) ? notifications : [])
      .filter((item) => !item?.read && notificationRouteMatchesPath(item, location.pathname))
      .map((item) => item?.id)
      .filter(Boolean);

    if (unreadRouteIds.length) {
      markPharmacyNotificationsRead(unreadRouteIds);
    }
  }, [location.pathname, notifications]);

  const inventorySnapshot = React.useMemo(
    () =>
      buildInventorySnapshot(profile, {
        inventoryRowsOverride: inventoryRows,
        fallbackInventoryRows: [],
        fallbackFeaturedItems: [],
      }),
    [inventoryRows, profile],
  );
  const currencyCode = resolveCurrencyCode(profile);

  const refreshInventory = React.useCallback(async () => {
    if (!currentUserId && !currentUserEmail) {
      setInventoryRows([]);
      setInventoryLoading(false);
      return;
    }

    setInventoryLoading(true);
    setInventoryError("");
    try {
      const nextRows = await listPharmacyInventoryItems({
        userId: currentUserId,
        email: currentUserEmail,
        profile,
      });
      if (activeActorRef.current !== currentUserId) return;
      setInventoryRows(Array.isArray(nextRows) ? nextRows : []);
    } catch (error) {
      if (activeActorRef.current !== currentUserId) return;
      setInventoryRows([]);
      setInventoryError(
        error?.message ||
          t("pharmacy.inventory.errors.loadFailed", "We couldn’t load branch inventory yet."),
      );
    } finally {
      if (activeActorRef.current === currentUserId) setInventoryLoading(false);
    }
  }, [currentUserEmail, currentUserId, profile, t]);

  React.useEffect(() => {
    refreshInventory();
  }, [refreshInventory]);

  const refreshStockAlerts = React.useCallback(
    async (nextInventoryRows = inventorySnapshot.inventoryRows) => {
      const nextState = await recomputeStockAlerts({
        profile,
        inventoryRows: nextInventoryRows,
        fallbackQueue: [],
        currentUserId,
        currentUserEmail,
        shouldCommit: () => activeActorRef.current === currentUserId,
      });

      if (activeActorRef.current !== currentUserId) return [];

      setQueue(Array.isArray(nextState.queue) ? nextState.queue : []);
      setPriceDrafts((current) => {
        const next = { ...current };
        nextState.queue.forEach((row) => {
          const key = String(row?.fulfillmentId || "").trim();
          if (!key || next[key] != null) return;
          next[key] = row?.priceValue ? String(row.priceValue) : "";
        });
        return next;
      });
      setNotifications(Array.isArray(nextState.notifications) ? nextState.notifications : []);

      return nextState.queue;
    },
    [currentUserEmail, currentUserId, inventorySnapshot.inventoryRows, profile],
  );

  React.useEffect(() => {
    let cancelled = false;

    async function hydrateQueue() {
      if (!currentUserId && !currentUserEmail) {
        setQueue([]);
        setQueueLoading(true);
        return;
      }
      setQueueLoading(true);
      const nextState = await recomputeStockAlerts({
        profile,
        inventoryRows: inventorySnapshot.inventoryRows,
        fallbackQueue: [],
        currentUserId,
        currentUserEmail,
        shouldCommit: () => activeActorRef.current === currentUserId && !cancelled,
      });
      if (cancelled) return;
      setQueue(Array.isArray(nextState.queue) ? nextState.queue : []);
      setPriceDrafts((current) => {
        const next = { ...current };
        nextState.queue.forEach((row) => {
          const key = String(row?.fulfillmentId || "").trim();
          if (!key || next[key] != null) return;
          next[key] = row?.priceValue ? String(row.priceValue) : "";
        });
        return next;
      });
      if (!cancelled) {
        setNotifications(Array.isArray(nextState.notifications) ? nextState.notifications : []);
        setQueueLoading(false);
      }
    }

    hydrateQueue().catch(() => {
      if (!cancelled) setQueueLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [currentUserEmail, currentUserId, inventorySnapshot.inventoryRows, profile]);

  function updateQueueBusy(fulfillmentId, value) {
    setActionBusyById((current) => ({
      ...current,
      [fulfillmentId]: value,
    }));
  }

  function handlePriceDraftChange(fulfillmentId, value) {
    setPriceDrafts((current) => ({
      ...current,
      [fulfillmentId]: value,
    }));
  }

  function handleOpenPriceDialog(row) {
    const fulfillmentId = String(row?.fulfillmentId || "").trim();
    if (!fulfillmentId) return;
    setPricingDialogRow(row);
    setPricingDialogDraft(priceDrafts[fulfillmentId] ?? (row?.priceValue ? String(row.priceValue) : ""));
    setPricingDeliveryDraft(String(row?.deliveryFeeValue ?? 0));
    setPricingDeliveryConfirmed(false);
    setQueueActionError("");
    setPricingCurrencyMenuOpen(false);
    setPricingDialogOpen(true);
  }

  function handleClosePriceDialog() {
    setPricingDialogOpen(false);
    setPricingDialogRow(null);
    setPricingDialogDraft("");
    setPricingDeliveryDraft("0");
    setPricingDeliveryConfirmed(false);
    setPricingCurrencyMenuOpen(false);
  }

  async function handleFulfillmentAction(row, action, quoteOverride = null) {
    const fulfillmentId = String(row?.fulfillmentId || "").trim();
    if (!fulfillmentId) return;

    setQueueActionError("");
    updateQueueBusy(fulfillmentId, true);

    try {
      const payload = {
        fulfillment_id: fulfillmentId,
        action,
      };

      if (action === "set_price") {
        const sourceValue = quoteOverride?.medicineSubtotal ?? priceDrafts[fulfillmentId];
        const rawPrice = String(sourceValue || "").replace(/[^\d.]/g, "").trim();
        const parsedPrice = Number(rawPrice);
        if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
          throw new Error(
            t(
              "pharmacy.prescriptions.errors.invalidPrice",
              "Enter a valid price before saving.",
            ),
          );
        }
        const rawDelivery = String(quoteOverride?.deliveryFee ?? 0).replace(/[^\d.-]/g, "").trim();
        const parsedDelivery = Number(rawDelivery);
        if (!Number.isFinite(parsedDelivery) || parsedDelivery < 0) {
          throw new Error(t("pharmacy.prescriptions.errors.invalidDelivery", "Enter a valid delivery charge."));
        }
        if (row?.mode === "Delivery" && quoteOverride?.deliveryConfirmed !== true) {
          throw new Error(t("pharmacy.prescriptions.errors.confirmDelivery", "Confirm the delivery charge before requesting payment."));
        }
        payload.price = parsedPrice;
        payload.medicine_subtotal = parsedPrice;
        payload.delivery_fee = row?.mode === "Delivery" ? parsedDelivery : 0;
        payload.delivery_quote_confirmed = row?.mode === "Delivery" ? true : false;
        payload.currency = currencyCode;
      }

      await updatePharmacyFulfillment(payload);

      // A stock-alert refresh must not turn a successful quote into an
      // apparent failed save and invite a duplicate submission.
      await refreshStockAlerts(inventorySnapshot.inventoryRows).catch(() => {});
      return true;
    } catch (error) {
      setQueueActionError(
        error?.message ||
          t(
            "pharmacy.prescriptions.errors.updateFailed",
            "Could not update this prescription right now.",
          ),
      );
      return false;
    } finally {
      updateQueueBusy(fulfillmentId, false);
    }
  }

  async function handleConfirmPriceDialog() {
    const row = pricingDialogRow;
    const fulfillmentId = String(row?.fulfillmentId || "").trim();
    if (!row || !fulfillmentId) return;

    const saved = await handleFulfillmentAction(row, "set_price", {
      medicineSubtotal: pricingDialogDraft,
      deliveryFee: pricingDeliveryDraft,
      deliveryConfirmed: pricingDeliveryConfirmed,
    });
    if (saved) {
      setPriceDrafts((current) => ({ ...current, [fulfillmentId]: pricingDialogDraft }));
      handleClosePriceDialog();
    }
  }

  React.useEffect(() => {
    let cancelled = false;

    async function hydrateSalesSnapshot() {
      if (!currentUserId && !currentUserEmail) {
        setSalesLoading(true);
        return;
      }
      setSalesLoading(true);
      const nextSnapshot = await loadPharmacySalesSnapshot(profile, {
        inventoryRows: inventorySnapshot.inventoryRows,
        currentUserId,
        currentUserEmail,
      });
      const nextTransactions = nextSnapshot?.reportingUnavailable
        ? []
        : await listPharmacyPaymentTransactions({
            currentUserId,
            currentUserEmail,
            limit: 24,
          }).catch((error) => {
            console.warn("[AfriMed pharmacy] Branch transaction reporting unavailable", {
              currentUserId,
              currentUserEmail,
              reason: error?.message || "unknown",
            });
            return { reportingUnavailable: true };
          });
      if (!cancelled) {
        const transactionReportingUnavailable = Boolean(nextTransactions?.reportingUnavailable);
        setSalesSnapshot((current) => {
          const shouldPreserveCurrent = Boolean(current?.hasLiveData && !nextSnapshot?.hasLiveData);
          const baseSnapshot = shouldPreserveCurrent ? current : nextSnapshot;
          return {
            ...baseSnapshot,
            reportingUnavailable: shouldPreserveCurrent
              ? Boolean(current?.reportingUnavailable && transactionReportingUnavailable)
              : Boolean(nextSnapshot?.reportingUnavailable || transactionReportingUnavailable),
            reportingUnavailableReason: transactionReportingUnavailable
              ? "transaction_reporting_load_failed"
              : shouldPreserveCurrent
                ? current?.reportingUnavailableReason
                : nextSnapshot?.reportingUnavailableReason,
            transactions: Array.isArray(nextTransactions)
              ? nextTransactions
              : Array.isArray(baseSnapshot?.transactions)
                ? baseSnapshot.transactions
                : [],
          };
        });
        setSalesLoading(false);
      }
    }

    hydrateSalesSnapshot().catch((error) => {
      if (!cancelled) {
        console.warn("[AfriMed pharmacy] Branch reporting hydration failed", {
          currentUserId,
          currentUserEmail,
          reason: error?.message || "unknown",
        });
        setSalesSnapshot((current) =>
          current?.hasLiveData
            ? current
            : {
                hasLiveData: false,
                reportingUnavailable: true,
                reportingUnavailableReason: "reporting_hydration_failed",
                soldItems: null,
                medicineRevenue: null,
                deliveryCollected: null,
                afrimedFee: null,
                completedCount: null,
                consultationsCount: null,
                monthlyRevenueSeries: [],
                topMedicines: [],
                transactions: [],
                deliveryEnabled: Boolean(profile?.deliveryEnabled),
              },
        );
        setSalesLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [currentUserEmail, currentUserId, paymentsRefreshToken, profile, inventorySnapshot.inventoryRows]);

  React.useEffect(() => {
    function refreshPayments() {
      setPaymentsRefreshToken((current) => current + 1);
    }

    window.addEventListener("afrimed:transactions:changed", refreshPayments);
    return () => {
      window.removeEventListener("afrimed:transactions:changed", refreshPayments);
    };
  }, []);

  async function handleDeleteInventoryRow() {
    const row = deleteCandidate;
    if (!row || deletingInventory) return;

    setDeletingInventory(true);
    setInventoryError("");
    try {
      await deletePharmacyInventoryItem({
        userId: currentUserId,
        email: currentUserEmail,
        itemId: row.id,
      });
      const nextRows = inventoryRows.filter((item) => item.id !== row.id);
      setInventoryRows(nextRows);
      setDeleteCandidate(null);
      await refreshStockAlerts(nextRows);
    } catch (error) {
      setInventoryError(
        error?.message ||
          t(
            "pharmacy.inventory.errors.removeFailed",
            "We couldn’t remove this stock item yet.",
          ),
      );
    } finally {
      setDeletingInventory(false);
    }
  }

  function handlePaymentsExport() {
    const transactionRows = Array.isArray(salesSnapshot?.transactions)
      ? salesSnapshot.transactions.filter((row) => transactionRowMatchesQuery(row, searchQuery))
      : [];

    downloadCsv("pharmacy-payments.csv", [
      [
        "Created",
        "Status",
        "Patient",
        "Prescriber",
        "Reference",
        "Medicine subtotal",
        "Delivery fee",
        "AfriMed fee",
        "Pharmacy net",
        "Gross amount",
        "Currency",
      ],
      ...transactionRows.map((row) => [
        formatDateTime(row.createdAt),
        row.status,
        row.patientName,
        row.doctorName,
        row.reference,
        row.medicineSubtotal,
        row.deliveryFee,
        row.afrimedFee,
        row.pharmacyNet,
        row.grossAmount,
        row.currency,
      ]),
    ]);
  }

  const filteredInventoryRows = React.useMemo(
    () => inventorySnapshot.inventoryRows.filter((row) => inventoryRowMatchesQuery(row, searchQuery)),
    [inventorySnapshot.inventoryRows, searchQuery],
  );
  const filteredQueue = React.useMemo(
    () => queue.filter((row) => queueRowMatchesQuery(row, searchQuery)),
    [queue, searchQuery],
  );
  const filteredTransactions = React.useMemo(
    () =>
      (Array.isArray(salesSnapshot?.transactions) ? salesSnapshot.transactions : []).filter((row) =>
        transactionRowMatchesQuery(row, searchQuery),
      ),
    [salesSnapshot?.transactions, searchQuery],
  );
  const pageCopy = {
    dashboard: {
      title: t("pharmacy.nav.dashboard", "Dashboard"),
      actionArea: null,
      body: (
        <DashboardOverview
          navigate={navigate}
          t={t}
          profile={profile}
          queue={queue}
          inventorySnapshot={inventorySnapshot}
          salesSnapshot={salesSnapshot}
          currencyCode={currencyCode}
          salesLoading={salesLoading}
        />
      ),
    },
    inventory: {
      title: t("pharmacy.nav.inventory", "Inventory"),
      actionArea: null,
      body: (
        <InventorySection
          navigate={navigate}
          t={t}
          languageCode={languageCode}
          inventorySnapshot={inventorySnapshot}
          currencyCode={currencyCode}
          visibleRows={filteredInventoryRows}
          searchQuery={searchQuery}
          inventoryLoading={inventoryLoading}
          inventoryError={inventoryError}
          onEditRow={(row) => navigate(`/pharmacy/inventory/${row.id}/edit`)}
          onRequestDeleteRow={setDeleteCandidate}
        />
      ),
    },
    prescriptions: {
      title: t("pharmacy.nav.prescriptions", "Prescriptions"),
      actionArea: null,
      body: (
        <PrescriptionsSection
          navigate={navigate}
          t={t}
          queue={queue}
          visibleQueue={filteredQueue}
          searchQuery={searchQuery}
          priceDrafts={priceDrafts}
          actionBusyById={actionBusyById}
          onPriceDraftChange={handlePriceDraftChange}
          onOpenPriceDialog={handleOpenPriceDialog}
          onFulfillmentAction={handleFulfillmentAction}
          actionError={queueActionError}
          currencyCode={currencyCode}
          queueLoading={queueLoading}
        />
      ),
    },
    payments: {
      title: t("pharmacy.nav.payments", "Payments"),
      actionArea: null,
      body: (
        <PaymentsSection
          pricedPrescriptions={queue}
          t={t}
          salesSnapshot={salesSnapshot}
          currencyCode={currencyCode}
          visibleTransactions={filteredTransactions}
          searchQuery={searchQuery}
          profile={profile}
          onExportCsv={handlePaymentsExport}
        />
      ),
    },
    settings: {
      title: t("pharmacy.nav.settings", "Settings"),
      actionArea: <Button variant="tonal" onClick={() => navigate("/onboarding/pharmacy?edit=1&role=pharmacy")}>{t("pharmacy.settings.actions.editOnboarding", "Edit onboarding")}</Button>,
      body: <SettingsSection profile={profile} t={t} />,
    },
    help: {
      title: t("pharmacy.nav.help", "Help"),
      actionArea: null,
      body: <HelpSection t={t} />,
    },
  };

  const current = pageCopy[section] || pageCopy.dashboard;
  const showSettingsTopBar = section === "settings";

  return (
    <div className="min-h-dvh bg-[rgb(var(--clr-bg))] text-[rgb(var(--clr-on-surface))]">
      <PharmacyDesktopHeader section={section} notifications={notifications} />

      <main className="afrimed-rail afrimed-rail--comfortable [--rail-pad:12px] pt-4 pb-12 lg:[--rail-pad:var(--afrimed-rail-gutter-desktop)]">
        {showSettingsTopBar ? (
          <TopBar
            title={current.title}
            subtitle={t(
              "pharmacy.settings.topbar.subtitle",
              "Verification, settlement exports, delivery zones, and staff access.",
            )}
            onBack={() => navigate("/pharmacy/dashboard")}
            trailing={current.actionArea}
            trailingAlign="title"
            titleWrapperClassName="mt-2 mb-5"
          />
        ) : (
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3 xl:hidden">
            {current.actionArea}
          </div>
        )}
        {current.body}
      </main>

      <BasicDialogCritical
        open={Boolean(deleteCandidate)}
        title={t("pharmacy.inventory.deleteDialog.title", "Notice")}
        body={t(
          "pharmacy.inventory.deleteDialog.body",
          "Remove {item} from branch inventory?",
          {
            item: deleteCandidate?.item || t("pharmacy.common.item", "this item"),
          },
        )}
        cancelText={t("common.cancel", "Cancel")}
        okText={deletingInventory ? t("common.removing", "Removing…") : t("common.remove", "Remove")}
        onCancel={() => (deletingInventory ? null : setDeleteCandidate(null))}
        onOk={handleDeleteInventoryRow}
        disableBackdropClose={deletingInventory}
        cancelStyle={{
          background: "var(--state-primary-12)",
          color: "rgb(var(--afrimed-primary))",
        }}
        okStyle={{
          background: "var(--state-critical-12)",
          color: "var(--on-critical)",
        }}
      />

      <BasicDialog
        open={pricingDialogOpen}
        title={t("pharmacy.prescriptions.actions.setPrice", "Set price")}
        body={
          pricingDialogRow ? (
            <PharmacyPriceDialogBody
              t={t}
              row={pricingDialogRow}
              draft={pricingDialogDraft}
              onDraftChange={setPricingDialogDraft}
              deliveryDraft={pricingDeliveryDraft}
              onDeliveryDraftChange={(value) => {
                setPricingDeliveryDraft(value);
                setPricingDeliveryConfirmed(false);
              }}
              deliveryConfirmed={pricingDeliveryConfirmed}
              onDeliveryConfirmedChange={setPricingDeliveryConfirmed}
              error={queueActionError}
              currencyCode={currencyCode || "ZAR"}
              currencyMenuOpen={pricingCurrencyMenuOpen}
              onToggleCurrencyMenu={() => setPricingCurrencyMenuOpen((current) => !current)}
            />
          ) : null
        }
        cancelText={t("common.cancel", "Cancel")}
        okText={t("common.confirm", "Confirm")}
        onCancel={handleClosePriceDialog}
        onOk={handleConfirmPriceDialog}
      />

      <PharmacyPasswordLifecycleDialog
        user={auth?.user || null}
        userId={currentUserId}
        email={currentUserEmail}
      />
    </div>
  );
}
